// src/pages/projects/TimelinePage.jsx
// Extracted from App.js - Timeline page
// BRIO Migration: Using code "5a" (table dense + striped)
// HEADER ALIGNMENT FIX: Full-width header band with BrioFrame inside for centered content
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { ChevronDown, ChevronRight, Plus, Trash2, HelpCircle, Info, AlertTriangle, Eye, Pencil, Lock } from 'lucide-react';
import {
  BrioAnalyticsSurface,
  BrioByCode,
  BrioTable,
  BrioAlert,
  BrioFrame,
  BrioModal,
  BrioDatePicker,
  getTablePropsFromCode
} from '../../components/BrioComponents';
import EmailDraftModal from '../../components/EmailDraftModal';
import InvoicePipelineModal from '../../components/InvoicePipelineModal';
import RecalculationPreviewModal from '../../components/RecalculationPreviewModal';
import { getCurrentTotalFee } from '../../hooks/cashflowInvoiceUtils';
import { calculateDueDate, formatDateForEmail, getStageAbbreviation, getPartStageAbbreviation } from '../../utils/emailTemplates';
import useCommunicationLog from '../../hooks/useCommunicationLog';
import useRecommendedActions from '../../hooks/useRecommendedActions';
import { useTimelineVersions } from '../../hooks/useTimelineVersions';
import RecommendedActionsPanel, { ACTION_EVENT_TYPE_MAP } from '../../components/RecommendedActionsPanel';
import RecommendedNextActionsModal from '../../components/RecommendedNextActionsModal';
import EmailStatusMarker from '../../components/EmailStatusMarker';
import EmailSentConfirmationModal from '../../components/EmailSentConfirmationModal';
import { resolveTimelineTrigger, isFieldActive, deriveEmailStatusFromField, getTriggerRouting } from '../../utils/timelineTriggerEngine';
import { deriveRowDates, applyInvoicePaidDates } from '../../utils/invoiceClosureSync';


// Presentation code for this page (dense striped table for matrix-style data)
const PRESENTATION_CODE = '5a';

// Stable empty invoice summary — shared identity keeps effects that depend on
// `invoiceSummary` from re-running on every render while no summary applies.
const EMPTY_INVOICE_SUMMARY = {};

// ============================================================================
// COLUMN HEADER TOOLTIPS
// ============================================================================
const COLUMN_TOOLTIPS = {
  Contract: 'Reference date from the contract. No workflow triggered.',
  Planned: 'Internal planning date. Changing an existing date by more than the threshold triggers a planned date revised email via native mail.',
  Actual: 'Stage completion date. Entering a date triggers a stage completion approval request email via native mail.',
  Proforma: 'Proforma invoice date. Optional. Setting a date creates a RADAR notification for admin to send the proforma invoice.',
  'Appr.': 'Client approval date for stage closure. No workflow triggered.',
  Invoice: 'Date when the tax invoice is issued. Setting a date creates a RADAR notification for admin to send the tax invoice.',
  Paid: 'Recorded when payment is confirmed. Triggers a payment confirmed email via native mail.',
};

// ============================================================================
// CONTEXT HINTS — shown briefly after editing key fields
// ============================================================================
const CONTEXT_HINTS = {
  actualDate: 'Stage completion approval email will be suggested.',
  proformaInvoiceDate: 'RADAR notification for admin to send proforma invoice.',
  invoiceDate: 'RADAR notification for admin to send tax invoice.',
  paidDate: 'Payment confirmed email will be suggested.',
};

// ============================================================================
// TIMELINE CELL COLOUR MAPPING
// ============================================================================
// Returns a BRIO CSS class for the cell background based on column and state.
// Uses design tokens defined in brio-styles.css (.brio-cell--*).
// Only colours cells that have a value (or are flagged overdue).
//
// Mapping:
//   Contract  → brio-cell--neutral   (grey-light)
//   Planned   → brio-cell--info      (teal-light)
//   Actual    → brio-cell--warning   (orange-light)
//   Approved  → brio-cell--accent    (gold-light)
//   Invoice   → brio-cell--accent-2  (yellow-light)
//   Paid      → brio-cell--success   (green-light)
//   Overdue   → brio-cell--danger    (red-light)  — overrides column colour
// ============================================================================
const COLUMN_CELL_CLASSES = {
  contractDate:        'brio-cell--neutral',
  targetDate:          'brio-cell--info',
  actualDate:          'brio-cell--warning',
  proformaInvoiceDate: 'brio-cell--accent-2',
  clientApprovalDate:  'brio-cell--accent',
  invoiceDate:         'brio-cell--accent-2',
  paidDate:            'brio-cell--success',
};

function getTimelineCellColor({ columnKey, value, isOverdue }) {
  if (isOverdue) return 'brio-cell--danger';
  if (!value) return '';
  return COLUMN_CELL_CLASSES[columnKey] || '';
}

// ============================================================================
// SUB-COMPONENT: ColumnHeaderWithInfo
// ============================================================================
// Uses a CSS class (.tl-info-tip) for the tooltip popup so it appears
// immediately on hover without relying on native title attribute delay.
function ColumnHeaderWithInfo({ label, tooltip, note }) {
  return (
    <span style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: '1px' }}>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '3px' }}>
        {label}
        {tooltip && (
          <span className="tl-info-trigger" style={{ cursor: 'help', display: 'inline-flex', opacity: 0.45, flexShrink: 0, position: 'relative' }}>
            <Info className="w-3 h-3" />
            <span className="tl-info-tip">{tooltip}</span>
          </span>
        )}
      </span>
      {note && (
        <span style={{ fontSize: '8.5px', fontWeight: 300, color: '#aaa', fontFamily: 'Raleway, sans-serif', lineHeight: '1.4', whiteSpace: 'normal', wordBreak: 'break-word', textAlign: 'center' }}>
          {note}
        </span>
      )}
    </span>
  );
}

// ============================================================================
// SUB-COMPONENT: TimelineWorkflowGuide
// ============================================================================
function TimelineWorkflowGuide({ onClose }) {
  const guideItems = [
    { field: 'Contract', desc: 'Reference dates from the contract. No workflow triggered.', color: 'var(--brio-grey)' },
    { field: 'Planned', desc: 'Internal planning date. Changes to an existing date may require a timeline update email to the client.', color: 'var(--brio-grey)' },
    { field: 'Actual', desc: 'Stage completion date. Triggers a stage completion approval request email to the client via native mail.', color: 'var(--brio-orange, #DB6B5D)' },
    { field: 'Proforma', desc: 'Optional proforma invoice date. Creates a RADAR notification for admin to prepare and send the proforma invoice. Does not open the supervisor mail client.', color: 'var(--brio-gold, #9F902A)' },
    { field: 'Approved', desc: 'Client approval date for stage closure. Recorded for reference, no workflow triggered.', color: 'var(--brio-grey)' },
    { field: 'Invoiced', desc: 'Date when the tax invoice is issued. Creates a RADAR notification for admin to send the tax invoice. Does not open the supervisor mail client.', color: 'var(--brio-gold, #9F902A)' },
    { field: 'Paid', desc: 'Recorded when payment is confirmed. Triggers a payment confirmed and next stage commencement email via native mail.', color: 'var(--brio-green, #16a34a)' },
  ];

  return (
    <BrioModal title="Timeline Workflow Guide" onClose={onClose} width="sm">
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', padding: '4px 0' }}>
        {guideItems.map(item => (
          <div key={item.field} style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
            <span style={{
              minWidth: '68px', fontWeight: 600, fontSize: '11px',
              color: item.color, flexShrink: 0, paddingTop: '1px',
            }}>
              {item.field}
            </span>
            <span style={{ fontSize: '11px', color: 'var(--brio-text)', lineHeight: 1.45 }}>
              {item.desc}
            </span>
          </div>
        ))}
        <div style={{
          marginTop: '4px', padding: '8px 10px',
          background: 'var(--brio-grey-light, #f8fafc)',
          borderRadius: '4px', fontSize: '10px', color: 'var(--brio-grey)',
          lineHeight: 1.5,
        }}>
          <strong>Tip:</strong> Email status dots next to date fields show workflow progress — grey (no workflow), gold (pending), teal (draft opened), green (sent). Click a dot to manually update its status.
        </div>
      </div>
    </BrioModal>
  );
}

// ============================================================================
// SUB-COMPONENT: ContextHint
// ============================================================================
// A small fade-in/fade-out hint shown below a date cell after editing.
function ContextHint({ text }) {
  return (
    <div style={{
      fontSize: '9px', color: 'var(--brio-teal, #689581)',
      marginTop: '1px', lineHeight: 1.2,
      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
      animation: 'brio-hint-fade 4s ease-in-out forwards',
    }}>
      {text}
    </div>
  );
}

// ============================================================================
// SUB-COMPONENT: CompactDateInput
// ============================================================================
// Shows dates as YYYY-MM-DD text. Clicking opens a small dropdown popover with
// a visible <input type="date"> and a Delete button. This replaces the native
// showPicker() approach because iOS Safari's native date picker "Reset" button
// does not reliably fire onChange back to React.
//
// The Delete button directly calls onChange with '' — no native picker
// dependency, works identically on iOS Safari, macOS, Chrome, Firefox, Edge.
// ============================================================================
// Format a YYYY-MM-DD date as "06-Jan-2026" to match the editable BrioDatePicker
// display (see MONTH_LABELS in BrioComponents), used by the locked/read-only cells.
const BRIO_MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
function formatBrioDate(value) {
  if (!value) return '';
  const parts = String(value).split('-');
  if (parts.length !== 3) return String(value);
  const [y, m, d] = parts;
  const mi = parseInt(m, 10) - 1;
  if (Number.isNaN(mi) || mi < 0 || mi > 11) return String(value);
  return `${String(d).padStart(2, '0')}-${BRIO_MONTH_LABELS[mi]}-${y}`;
}

function CompactDateInput({ value, onChange, locked }) {
  // Wrap onChange to emit synthetic event for backwards compat with callers
  const handleChange = (v) => {
    onChange({ target: { value: v } });
  };

  // Locked mode (e.g. a paid stage that hasn't been unlocked for manual edit):
  // render a static, non-editable box instead of the date picker.
  if (locked) {
    return <LockedDateBox value={value} />;
  }

  return (
    <BrioDatePicker
      value={value || ''}
      onChange={handleChange}
      placeholder="—"
      clearable={!!value}
      style={{ width: '100%' }}
      className="brio-datepicker-trigger--compact"
    />
  );
}

// ============================================================================
// SUB-COMPONENT: LockedDateBox
// ============================================================================
// A static, non-editable date cell used for stages whose invoice status is Paid.
// The date stays visible but can only be changed after unlocking the stage via
// the edit (pencil) icon next to the stage's Status badge.
function LockedDateBox({ value }) {
  const formatted = formatBrioDate(value);
  return (
    <div
      title="Paid stage — locked. Use the edit (pencil) icon next to the Status badge to make changes."
      style={{
        width: '100%', height: '22px', boxSizing: 'border-box',
        border: '1px solid var(--brio-border)', padding: '0 4px',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: '9px', fontFamily: 'var(--brio-font-family)',
        color: formatted ? 'var(--brio-text, #333)' : 'var(--brio-grey)',
        background: 'var(--brio-grey-light, #f8fafc)', cursor: 'not-allowed',
      }}
    >
      {formatted || '—'}
    </div>
  );
}

// ============================================================================
// SUB-COMPONENT: LockToggleButton
// ============================================================================
// The pencil/lock toggle shown next to the Status badge of a paid stage/part.
// Locked → pencil (click to edit dates manually); unlocked → lock (click to re-lock).
function LockToggleButton({ locked, onToggle, name }) {
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onToggle(); }}
      title={locked
        ? `${name} is paid — click to edit its dates manually`
        : `Editing ${name} — click to lock its dates again`}
      style={{
        background: 'none', border: 'none', cursor: 'pointer', padding: '0 2px',
        lineHeight: 1, flexShrink: 0, display: 'inline-flex', alignItems: 'center',
        color: locked ? 'var(--brio-grey)' : 'var(--brio-gold)',
      }}
    >
      {locked ? <Pencil className="w-2.5 h-2.5" /> : <Lock className="w-2.5 h-2.5" />}
    </button>
  );
}

// ============================================================================
// SUB-COMPONENT: LockedDot
// ============================================================================
// A red status dot shown on the Planned/Actual columns of a locked (paid) stage,
// flagging that the date is frozen and no longer tracks the planning-page sync.
function LockedDot({ title }) {
  return (
    <span
      title={title || 'Locked — this date is frozen and no longer syncs from the planning page'}
      style={{
        width: '8px', height: '8px', borderRadius: '50%',
        background: 'var(--brio-red, #DB6B5D)', border: '1.5px solid var(--brio-red, #DB6B5D)',
        marginLeft: '3px', flexShrink: 0, display: 'inline-block',
      }}
    />
  );
}

// ============================================================================
// SUB-COMPONENT: ActualStatusMarker
// ============================================================================
// The dot in the Actual column. It is RED whenever an actual date is recorded but does NOT match the
// stage's planned (target) date ("dates don't match"), GREY only when no actual date is recorded
// ("not updated to actuals"), and otherwise the normal email-status marker (gold when set / in sync,
// green once emailed). This applies to EVERY stage — paid/frozen or not — since it is the date
// mismatch, not the lock state, that turns the dot red.
function ActualStatusMarker({ actualDate, plannedDate, status, onChange }) {
  const actual = actualDate ? String(actualDate).trim() : '';
  const planned = plannedDate ? String(plannedDate).trim() : '';
  if (actual && planned && actual !== planned) {
    return (
      <span
        title={`Actual date (${actual}) doesn't match the planned date (${planned})`}
        style={{
          width: '8px', height: '8px', borderRadius: '50%',
          background: 'var(--brio-red, #DB6B5D)', border: '1.5px solid var(--brio-red, #DB6B5D)',
          marginLeft: '3px', flexShrink: 0, display: 'inline-block',
        }}
      />
    );
  }
  return <EmailStatusMarker status={status} onChange={onChange} />;
}

// ============================================================================
// HELPER: derivePartStageKey / deriveCCStageKey
// ============================================================================
// Used for linking part stages and CC months to InvoicesPage via stage_key.
// ============================================================================
function derivePartStageKey(parentKey, partId) {
  return `${parentKey}__part__${partId}`;
}

function deriveCCStageKey(invoiceMonth) {
  // invoiceMonth is YYYY-MM-01 or YYYY-MM; normalize to YYYY-MM
  return `cc__${(invoiceMonth || '').slice(0, 7)}`;
}

// ============================================================================
// HELPER: generatePartStageId
// ============================================================================
function generatePartStageId() {
  return Date.now().toString(36) + Math.random().toString(36).slice(2, 6);
}

// ============================================================================
// HELPER: dispatchInvoiceCreateIntent
// ============================================================================
// Fires a CustomEvent so InvoicesPage can pick up the intent and call openCreate.
// ============================================================================

// Returns { days: Set<number>, holidaySet: Set<string> } from a project's stageTimeline.
// Falls back to Mon–Fri, no holidays when settings aren't stored.
function getWorkingDaysConfig(stageTimeline) {
  const presets = stageTimeline?._settingsPresets;
  const activeId = stageTimeline?._activePresetId;
  let wd = null;
  if (Array.isArray(presets) && presets.length > 0) {
    const active = (activeId && presets.find(p => p.id === activeId)) || presets[0];
    wd = active?.workingDays || null;
  }
  const days = new Set(wd?.days ?? [1, 2, 3, 4, 5]);
  const holidaySet = new Set((wd?.holidays || []).map(h => h.date));
  return { days, holidaySet };
}

// Adds `count` working days (Mon–Fri minus BRIO holidays) to a YYYY-MM-DD date string.
function addWorkDaysToDate(dateStr, count, stageTimeline) {
  if (!dateStr || !count || count <= 0) return dateStr;
  const { days: workDays, holidaySet } = getWorkingDaysConfig(stageTimeline);
  let cur = new Date(dateStr + 'T00:00:00');
  let remaining = count;
  while (remaining > 0) {
    cur = new Date(cur.getTime() + 86400000);
    const dow = cur.getDay();
    const iso = `${cur.getFullYear()}-${String(cur.getMonth() + 1).padStart(2, '0')}-${String(cur.getDate()).padStart(2, '0')}`;
    if (workDays.has(dow) && !holidaySet.has(iso)) remaining--;
  }
  return `${cur.getFullYear()}-${String(cur.getMonth() + 1).padStart(2, '0')}-${String(cur.getDate()).padStart(2, '0')}`;
}

function dispatchInvoiceCreateIntent(projectId, stageKey, invoiceDate) {
  window.dispatchEvent(new CustomEvent('brio:invoiceCreateIntent', {
    detail: { projectId, stageKey, invoiceDate },
  }));
}

// ============================================================================
// HELPER: getStageStatusFromTimeline
// ============================================================================
// Shared status logic for full stages AND part stages (same field precedence).
// ============================================================================
function getStageStatusFromTimeline(timeline, invoiceSummaryEntry) {
  // Invoice-confirmed paid takes precedence
  if (invoiceSummaryEntry?.hasPaidConfirmed) return { status: 'Paid', color: 'brio-badge--approved' };
  if (timeline.paidDate) return { status: 'Paid', color: 'brio-badge--approved' };
  // A timeline invoiceDate that actually belongs to a proforma invoice (proforma
  // exists, no tax invoice) is not a tax-invoice signal — fall through to Proforma.
  const proformaOnly = invoiceSummaryEntry?.hasProformaInvoice && !invoiceSummaryEntry?.hasIssuedInvoice;
  if (invoiceSummaryEntry?.hasIssuedInvoice || (timeline.invoiceDate && !proformaOnly)) {
    // Only show "Invoiced" if admin confirmed the invoice was sent;
    // otherwise show "Radar" (pending admin action to send the invoice)
    const invoiceEmailStatus = timeline.emailStatus?.invoiceDate;
    if (invoiceEmailStatus === 'sent') return { status: 'Invoiced', color: 'brio-badge--warning' };
    return { status: 'Radar', color: 'brio-badge--info' };
  }
  // Proforma invoice issued (or a proforma date recorded) but no tax invoice yet
  if (invoiceSummaryEntry?.hasProformaInvoice || timeline.proformaInvoiceDate) {
    return { status: 'Proforma', color: 'brio-badge--info' };
  }
  if (timeline.clientApprovalDate) return { status: 'Approved', color: 'brio-badge--pending' };
  if (timeline.actualDate) return { status: 'Completed', color: 'brio-badge--pending' };
  if (timeline.targetDate) return { status: 'Planned', color: 'brio-badge--pending' };
  if (timeline.contractDate) return { status: 'Contract', color: '' };
  return { status: '-', color: '' };
}

// ============================================================================
// HELPER: getCCStatus
// ============================================================================
// Derives CC lifecycle status from stageTimeline.cc data.
// Returns: 'inactive' | 'active' | 'paused' | 'stopped'
// ============================================================================
function getCCStatus(ccTimeline) {
  if (!ccTimeline || !ccTimeline.startDate) return 'inactive';
  const today = new Date().toISOString().slice(0, 10);
  if (ccTimeline.stopDate && ccTimeline.stopDate <= today) return 'stopped';
  const pauses = ccTimeline.pausedPeriods || [];
  const openPause = pauses.find(p => p.from && !p.to);
  if (openPause) return 'paused';
  return 'active';
}

function getCCStatusBadge(status) {
  switch (status) {
    case 'active': return { label: 'Active', color: 'brio-badge--approved' };
    case 'paused': return { label: 'Paused', color: 'brio-badge--warning' };
    case 'stopped': return { label: 'Stopped', color: 'brio-badge--rejected' };
    default: return { label: 'Inactive', color: '' };
  }
}

// ============================================================================
// HELPER: generateCCInvoices
// ============================================================================
// Auto-generates monthly invoice rows for CC based on start/pause/stop.
// Preserves existing paidDate values using invoiceMonth as stable key.
// ============================================================================
function generateCCInvoices(ccTimeline, monthlyFee) {
  if (!ccTimeline?.startDate || !monthlyFee) return [];

  const existing = ccTimeline.invoices || [];
  const paidMap = {};
  existing.forEach(inv => {
    if (inv.paidDate) paidMap[inv.invoiceMonth] = inv.paidDate;
  });

  const pauses = ccTimeline.pausedPeriods || [];

  const toYM = (d) => d.toISOString().slice(0, 7);

  const start = new Date(ccTimeline.startDate + 'T00:00:00Z');

  let end;
  if (ccTimeline.stopDate) {
    end = new Date(ccTimeline.stopDate + 'T00:00:00Z');
  } else {
    end = new Date();
    end.setUTCMonth(end.getUTCMonth() + 3);
  }
  // Normalize end to first-of-month for clean comparison
  end = new Date(toYM(end) + '-01T00:00:00Z');

  const isMonthPaused = (ym) => {
    const monthStart = ym + '-01';
    const y = parseInt(ym.slice(0, 4), 10);
    const m = parseInt(ym.slice(5, 7), 10);
    const lastDay = new Date(Date.UTC(y, m, 0)).getUTCDate();
    const monthEnd = `${ym}-${String(lastDay).padStart(2, '0')}`;

    return pauses.some(p => {
      const pFrom = p.from;
      const pTo = p.to || '9999-12-31';
      return pFrom <= monthEnd && pTo >= monthStart;
    });
  };

  const invoices = [];
  const cursor = new Date(toYM(start) + '-01T00:00:00Z');

  let guard = 0;
  while (cursor <= end && guard < 240) {
    const ym = toYM(cursor);
    const invMonth = ym + '-01';

    if (!isMonthPaused(ym)) {
      invoices.push({
        invoiceMonth: invMonth,
        invoiceDate: invMonth,
        amount: Number(monthlyFee),
        paid: !!paidMap[invMonth],
        paidDate: paidMap[invMonth] || null,
      });
    }

    cursor.setUTCMonth(cursor.getUTCMonth() + 1);
    guard += 1;
  }

  return invoices;
}

// ============================================================================
// SUB-COMPONENT: CCLifecyclePanel
// ============================================================================
// Renders a table of CC monthly invoice rows (Contract / Invoice / Paid columns)
// followed by a compact footer with lifecycle controls.
// ============================================================================
function CCLifecyclePanel({ ccTimeline, onUpdate, onResetCC, isAdminOrSupervisor, isAdmin, monthlyFee, fmtCurr, invoiceSummary, projectId, onOpenInvoiceWorkflow }) {
  const [dateInput, setDateInput] = React.useState('');
  const status = getCCStatus(ccTimeline);
  const pauses = (ccTimeline?.pausedPeriods || []);

  // Memoize invoice generation — only recomputes when inputs change, not on every render
  const invoices = React.useMemo(
    () => generateCCInvoices(ccTimeline, monthlyFee),
    [ccTimeline, monthlyFee]
  );

  const updateCC = (patch) => {
    const base = { ...(ccTimeline || {}), ...patch };
    if (!base.pausedPeriods) base.pausedPeriods = [];
    // Regenerate invoices whenever lifecycle changes (preserves paid flags via invoiceMonth key)
    const regenerated = generateCCInvoices(base, monthlyFee);
    onUpdate({ ...base, invoices: regenerated });
  };

  const handleStart = () => {
    if (!dateInput) return;
    updateCC({ startDate: dateInput, stopDate: null, pausedPeriods: [] });
    setDateInput('');
  };

  const handlePause = () => {
    if (!dateInput) return;
    if (ccTimeline?.startDate && dateInput < ccTimeline.startDate) {
      alert('Pause date must be on or after start date.');
      return;
    }
    const openPause = pauses.find(p => !p.to);
    if (openPause) { alert('Already paused.'); return; }
    updateCC({ pausedPeriods: [...pauses, { from: dateInput, to: null }] });
    setDateInput('');
  };

  const handleResume = () => {
    if (!dateInput) return;
    const openPause = pauses.find(p => !p.to);
    if (!openPause) return;
    if (dateInput < openPause.from) {
      alert('Resume date must be on or after pause start (' + openPause.from + ').');
      return;
    }
    const updated = pauses.map(p => (!p.to ? { ...p, to: dateInput } : p));
    updateCC({ pausedPeriods: updated });
    setDateInput('');
  };

  const handleStop = () => {
    if (!dateInput) return;
    if (ccTimeline?.startDate && dateInput < ccTimeline.startDate) {
      alert('Stop date must be on or after start date.');
      return;
    }
    const closedPauses = pauses.map(p => (!p.to ? { ...p, to: dateInput } : p));
    updateCC({ stopDate: dateInput, pausedPeriods: closedPauses });
    setDateInput('');
  };

  // Update invoice date for a CC month
  const updateInvoiceDate = (invoiceMonth, newDate) => {
    const updatedInvoices = invoices.map(inv => {
      if (inv.invoiceMonth !== invoiceMonth) return inv;
      return { ...inv, invoiceDate: newDate || null };
    });
    onUpdate({ ...(ccTimeline || {}), invoices: updatedInvoices });
  };

  // Update paid date for an invoice month
  const updatePaidDate = (invoiceMonth, newDate) => {
    const updatedInvoices = invoices.map(inv => {
      if (inv.invoiceMonth !== invoiceMonth) return inv;
      return { ...inv, paidDate: newDate || null, paid: !!newDate };
    });
    onUpdate({ ...(ccTimeline || {}), invoices: updatedInvoices });
  };

  // Format YYYY-MM-DD (pass-through)
  const fmtDate = (d) => {
    if (!d) return '—';
    const parts = d.split('-');
    return parts.length === 3 ? `${parts[0]}-${parts[1]}-${parts[2]}` : d;
  };

  if (!isAdminOrSupervisor) {
    return (
      <div style={{ padding: '8px 4px' }}>
        <div className="brio-text-xs brio-text-grey">
          {ccTimeline?.startDate ? `Started: ${ccTimeline.startDate}` : 'Not started'}
          {status === 'paused' && ` · Paused since ${pauses.find(p => !p.to)?.from}`}
          {status === 'stopped' && ` · Stopped: ${ccTimeline.stopDate}`}
        </div>
      </div>
    );
  }

  return (
    <div style={{ paddingBottom: '8px' }}>
      {/* ── Monthly invoice rows table ── */}
      {invoices.length > 0 && (
        <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
          <thead>
            <tr style={{ background: 'var(--brio-grey-light)' }}>
              <th style={{ padding: '3px 6px', fontSize: '9px', fontWeight: 600, textAlign: 'left', width: '90px' }}>Contract</th>
              <th style={{ padding: '3px 6px', fontSize: '9px', fontWeight: 600, textAlign: 'left', width: '90px' }}>Invoice</th>
              <th style={{ padding: '3px 6px', fontSize: '9px', fontWeight: 600, textAlign: 'left', width: '90px' }}>Paid</th>
            </tr>
          </thead>
          <tbody>
            {invoices.map((inv) => {
              const ccMonthKey = deriveCCStageKey(inv.invoiceMonth);
              const monthSummary = invoiceSummary?.[ccMonthKey];
              const issuedInvDate = monthSummary?.lastIssuedInvoiceDate || monthSummary?.lastInvoiceDate;
              const hasIssuedInvoice = monthSummary?.hasIssuedInvoice && issuedInvDate;
              const hasPaidConfirmed = monthSummary?.hasPaidConfirmed;
              const confirmedPaidDate = hasPaidConfirmed ? (monthSummary.paidDate || inv.paidDate || '') : null;

              return (
                <tr key={inv.invoiceMonth} className="brio-row--hoverable">
                  {/* Contract = first day of month, read-only */}
                  <td className={getTimelineCellColor({ columnKey: 'contractDate', value: inv.invoiceMonth })}>
                    <div style={{
                      width: '100%', height: '22px', boxSizing: 'border-box', padding: '0 4px',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: '9px', fontFamily: 'var(--brio-font-family)',
                    }}>
                      {fmtDate(inv.invoiceMonth)}
                    </div>
                  </td>
                  {/* Invoice date — click triggers tax invoice workflow */}
                  <td
                    className={getTimelineCellColor({ columnKey: 'invoiceDate', value: issuedInvDate || inv.invoiceDate })}
                    onClick={() => {
                      if (onOpenInvoiceWorkflow) {
                        const dt = new Date(inv.invoiceMonth + 'T00:00:00');
                        const label = `CC ${dt.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}`;
                        onOpenInvoiceWorkflow(ccMonthKey, label, inv.invoiceDate || inv.invoiceMonth);
                      }
                    }}
                    style={{ cursor: onOpenInvoiceWorkflow ? 'pointer' : undefined }}
                  >
                    {hasIssuedInvoice && !isAdminOrSupervisor ? (
                      <div title="Invoice date from Invoices page" style={{
                        width: '100%', height: '22px', boxSizing: 'border-box', border: '1px solid var(--brio-border)', padding: '0 4px',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: '9px', fontFamily: 'var(--brio-font-family)',
                        background: 'var(--brio-gold-light, #fef9e7)', color: 'var(--brio-gold, #9F902A)',
                      }}>
                        {fmtDate(issuedInvDate)}
                      </div>
                    ) : (
                      <CompactDateInput
                        value={inv.invoiceDate || ''}
                        onChange={(e) => { e.stopPropagation(); updateInvoiceDate(inv.invoiceMonth, e.target.value); }}
                      />
                    )}
                  </td>
                  {/* Paid date */}
                  <td className={getTimelineCellColor({ columnKey: 'paidDate', value: inv.paidDate || hasPaidConfirmed })}>
                    {(hasPaidConfirmed && !isAdminOrSupervisor) ? (
                      <div title="Payment confirmed via Invoices page — locked" style={{
                        width: '100%', height: '22px', boxSizing: 'border-box', border: '1px solid var(--brio-border)', padding: '0 4px',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: '9px', fontFamily: 'var(--brio-font-family)',
                        background: 'var(--brio-green-light, #f0fdf4)', color: 'var(--brio-green, #16a34a)',
                        cursor: 'not-allowed',
                      }}>
                        ✓ {fmtDate(confirmedPaidDate)}
                      </div>
                    ) : (
                      <CompactDateInput
                        value={hasPaidConfirmed ? (confirmedPaidDate || inv.paidDate || '') : (inv.paidDate || '')}
                        onChange={(e) => updatePaidDate(inv.invoiceMonth, e.target.value)}
                      />
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      {invoices.length === 0 && ccTimeline?.startDate && (
        <div className="brio-text-xs brio-text-grey" style={{ padding: '6px 8px' }}>
          No CC months generated yet.
        </div>
      )}

      {/* ── Footer: lifecycle controls + summary ── */}
      <div className="brio-flex brio-items-center brio-flex-wrap brio-gap-xs"
        style={{ padding: '6px 8px', borderTop: '1px solid var(--brio-border)', marginTop: invoices.length > 0 ? '0' : '4px' }}>
        <BrioDatePicker
          value={dateInput}
          onChange={v => setDateInput(v)}
          clearable={false}
          placeholder="Date"
        />
        {(status === 'inactive' || status === 'stopped') && (
          <button onClick={handleStart} disabled={!dateInput}
            className="brio-btn brio-btn-primary"
            style={{ padding: '4px 10px', fontSize: '10px', opacity: dateInput ? 1 : 0.5 }}>
            {status === 'stopped' ? 'Restart' : 'Start'}
          </button>
        )}
        {status === 'active' && (
          <>
            <button onClick={handlePause} disabled={!dateInput}
              className="brio-btn brio-btn-secondary"
              style={{ padding: '4px 10px', fontSize: '10px', opacity: dateInput ? 1 : 0.5 }}>
              Pause
            </button>
            <button onClick={handleStop} disabled={!dateInput}
              className="brio-btn brio-btn-secondary"
              style={{ padding: '4px 10px', fontSize: '10px', opacity: dateInput ? 1 : 0.5, color: 'var(--brio-red)' }}>
              Stop
            </button>
          </>
        )}
        {status === 'paused' && (
          <>
            <button onClick={handleResume} disabled={!dateInput}
              className="brio-btn brio-btn-primary"
              style={{ padding: '4px 10px', fontSize: '10px', opacity: dateInput ? 1 : 0.5 }}>
              Resume
            </button>
            <button onClick={handleStop} disabled={!dateInput}
              className="brio-btn brio-btn-secondary"
              style={{ padding: '4px 10px', fontSize: '10px', opacity: dateInput ? 1 : 0.5, color: 'var(--brio-red)' }}>
              Stop
            </button>
          </>
        )}
        <span className="brio-text-xs" style={{ marginLeft: '2px' }}>
          {ccTimeline?.startDate && <span>Started: <strong>{ccTimeline.startDate}</strong></span>}
          {ccTimeline?.stopDate && <span> · Stopped: <strong>{ccTimeline.stopDate}</strong></span>}
          {status === 'paused' && <span> · Paused since <strong>{pauses.find(p => !p.to)?.from}</strong></span>}
          {monthlyFee > 0 && <span> · Fee: <strong>{fmtCurr(monthlyFee)}/mo</strong></span>}
        </span>
        {ccTimeline?.startDate && (
          <button
            onClick={() => {
              if (!window.confirm('Clear CC start date and remove CC monthly generation? Existing invoices will NOT be deleted.')) return;
              onResetCC();
            }}
            className="brio-btn brio-btn-secondary"
            style={{ padding: '3px 10px', fontSize: '9px', color: 'var(--brio-red)', marginLeft: 'auto' }}
          >
            Reset CC Start Date
          </button>
        )}
      </div>
      {/* Pause history */}
      {pauses.length > 0 && (
        <div style={{ padding: '4px 8px 0 8px' }}>
          <div className="brio-text-xs brio-font-medium" style={{ marginBottom: '2px' }}>Pause History</div>
          {pauses.map((p, i) => (
            <div key={i} className="brio-text-xs brio-text-grey" style={{ padding: '1px 0' }}>
              {p.from} → {p.to || '(ongoing)'}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// TIMELINE VERSIONS — read-only snapshots
// ============================================================================

// Format an ISO timestamp for a version label, e.g. "12 Aug 2026".
function formatVersionDate(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch {
    return String(iso);
  }
}

// Resolve the 7 displayed date-column values for a stage/part/misc timeline object,
// mirroring the live table's resolution (invoice-summary-derived dates win where present).
// Returns plain strings ('' when empty) so the snapshot is fully self-contained.
function resolveTimelineDisplayDates(tl, summary) {
  const t = tl || {};
  const s = summary || null;
  const invDate = s?.lastIssuedInvoiceDate || s?.lastInvoiceDate || null;
  const proformaOnly = s?.hasProformaInvoice && !s?.hasIssuedInvoice;
  const timelineInvDate = proformaOnly ? '' : (t.invoiceDate || '');
  const issuedProformaDate = s?.lastIssuedProformaDate || null;
  const proformaDisplay = issuedProformaDate || s?.lastProformaInvoiceDate || t.proformaInvoiceDate || '';
  const paidDisplay = s?.hasPaidConfirmed ? (s?.paidDate || t.paidDate || '') : (t.paidDate || '');
  return {
    contractDate: t.contractDate || '',
    targetDate: t.targetDate || '',
    actualDate: t.actualDate || '',
    proformaInvoiceDate: proformaDisplay,
    clientApprovalDate: t.clientApprovalDate || '',
    invoiceDate: invDate || timelineInvDate || '',
    paidDate: paidDisplay,
  };
}

// Freeze the visible part-stages of a stage into snapshot rows (same filter/sort as live).
function buildSnapshotParts(parentKey, tl, invoiceSummary, showAllocatedOnly) {
  const n = (v) => Number(v ?? 0) || 0;
  return (tl.partStages || [])
    .filter((part) => {
      if (!showAllocatedOnly) return true;
      const pb = part.billing || {};
      return n(pb.percentOfStage) > 0 || n(pb.lumpsum ?? pb.lumpSum) > 0;
    })
    .slice()
    .sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { numeric: true, sensitivity: 'base' }))
    .map((part) => {
      const partKey = `${parentKey}__part__${part.id}`;
      const ps = (invoiceSummary || {})[partKey];
      const s = getStageStatusFromTimeline(part, ps);
      return {
        name: part.name || '',
        dates: resolveTimelineDisplayDates(part, ps),
        status: { label: s.label || s.status, color: s.color || '' },
      };
    });
}

// A single read-only date cell that visually matches an editable date input but is static.
function ReadOnlyDateBox({ columnKey, value }) {
  return (
    <td className={getTimelineCellColor({ columnKey, value })}>
      <div style={{
        width: '100%', height: '22px', boxSizing: 'border-box',
        border: '1px solid var(--brio-border)', padding: '0 4px',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: '9px', fontFamily: 'var(--brio-font-family)',
        color: value ? 'var(--brio-text, #333)' : 'var(--brio-grey)',
        background: 'transparent',
      }}>
        {formatBrioDate(value) || '—'}
      </div>
    </td>
  );
}

// ============================================================================
// SUB-COMPONENT: ReadOnlyTimelineTable
// ============================================================================
// Renders a frozen snapshot (built by buildCurrentSnapshot) as a view-only replica
// of the timeline table. No inputs, no handlers — dates/status are not editable and
// are never affected by later changes to the live timeline.
function ReadOnlyTimelineTable({ snapshot, tableProps, versionLabel, createdAt, onExit }) {
  const rows = Array.isArray(snapshot?.rows) ? snapshot.rows : [];
  return (
    <div>
      {/* Read-only banner */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: '8px',
        padding: '6px 10px', marginBottom: '6px',
        background: 'var(--brio-gold-light)', border: '1px solid var(--brio-gold)',
        fontSize: '11px', color: 'var(--brio-text, #333)',
      }}>
        <Eye className="w-3 h-3" style={{ color: 'var(--brio-gold)', flexShrink: 0 }} />
        <span>
          Viewing <strong>{versionLabel}</strong>
          {createdAt ? ` · saved ${formatVersionDate(createdAt)}` : ''} — read-only snapshot
        </span>
        {onExit && (
          <button
            type="button"
            onClick={onExit}
            style={{
              marginLeft: 'auto', flexShrink: 0, background: '#fff',
              border: '1px solid var(--brio-gold)', color: 'var(--brio-gold)',
              cursor: 'pointer', padding: '2px 8px', fontSize: '10px',
              fontFamily: 'Raleway, sans-serif', borderRadius: '2px',
            }}
          >
            Return to current
          </button>
        )}
      </div>
      <BrioByCode code={PRESENTATION_CODE} className="!overflow-visible">
        <BrioTable {...tableProps}>
          <thead>
            <tr>
              <th style={{ width: 24 }}></th>
              <th style={{ minWidth: 70 }}>Stage</th>
              <th style={{ minWidth: 58 }}><ColumnHeaderWithInfo label="Contract" tooltip={COLUMN_TOOLTIPS.Contract} note="(Based on contract terms)" /></th>
              <th style={{ minWidth: 58 }}><ColumnHeaderWithInfo label="Planned" tooltip={COLUMN_TOOLTIPS.Planned} note="(Current planning/ target date)" /></th>
              <th style={{ minWidth: 58 }}><ColumnHeaderWithInfo label="Actual" tooltip={COLUMN_TOOLTIPS.Actual} note="(Actual stage completion)" /></th>
              <th style={{ minWidth: 58 }}><ColumnHeaderWithInfo label="Proforma" tooltip={COLUMN_TOOLTIPS.Proforma} /></th>
              <th style={{ minWidth: 58 }}><ColumnHeaderWithInfo label="Appr." tooltip={COLUMN_TOOLTIPS['Appr.']} /></th>
              <th style={{ minWidth: 58 }}><ColumnHeaderWithInfo label="Invoice" tooltip={COLUMN_TOOLTIPS.Invoice} /></th>
              <th style={{ minWidth: 58 }}><ColumnHeaderWithInfo label="Paid" tooltip={COLUMN_TOOLTIPS.Paid} /></th>
              <th style={{ minWidth: 70 }}>Status</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td colSpan={10} className="brio-text-xs brio-text-grey" style={{ textAlign: 'center', padding: '12px' }}>
                  No stages captured in this snapshot.
                </td>
              </tr>
            )}
            {rows.map((row) => {
              if (row.kind === 'cc') {
                return (
                  <tr key={row.key} className="brio-row--hoverable">
                    <td />
                    <td className="brio-font-medium"><span title={row.name}>{getStageAbbreviation(row.name)}</span></td>
                    <td colSpan={5} className="brio-text-xs brio-text-grey" style={{ textAlign: 'center' }}>{row.ccSummaryText}</td>
                    <ReadOnlyDateBox columnKey="invoiceDate" value={row.ccDates?.invoiceDate} />
                    <ReadOnlyDateBox columnKey="paidDate" value={row.ccDates?.paidDate} />
                    <td><span className={`brio-badge ${row.status?.color || ''}`}>{row.status?.label}</span></td>
                  </tr>
                );
              }
              const d = row.dates || {};
              return (
                <React.Fragment key={row.key}>
                  <tr className="brio-row--hoverable">
                    <td />
                    <td className="brio-font-medium"><span title={row.name}>{getStageAbbreviation(row.name)}</span></td>
                    <ReadOnlyDateBox columnKey="contractDate" value={d.contractDate} />
                    <ReadOnlyDateBox columnKey="targetDate" value={d.targetDate} />
                    <ReadOnlyDateBox columnKey="actualDate" value={d.actualDate} />
                    <ReadOnlyDateBox columnKey="proformaInvoiceDate" value={d.proformaInvoiceDate} />
                    <ReadOnlyDateBox columnKey="clientApprovalDate" value={d.clientApprovalDate} />
                    <ReadOnlyDateBox columnKey="invoiceDate" value={d.invoiceDate} />
                    <ReadOnlyDateBox columnKey="paidDate" value={d.paidDate} />
                    <td><span className={`brio-badge ${row.status?.color || ''}`}>{row.status?.label}</span></td>
                  </tr>
                  {(row.parts || []).map((part, i) => {
                    const pd = part.dates || {};
                    return (
                      <tr key={`${row.key}__part__${i}`} style={{ background: 'var(--brio-grey-light)', fontSize: '10px' }}>
                        <td />
                        <td style={{ paddingLeft: '20px' }}>
                          <div className="brio-flex brio-items-center" style={{ gap: '2px' }}>
                            <span className="brio-text-grey" style={{ fontSize: '9px' }}>↳</span>
                            <span className="brio-font-medium" style={{ fontSize: '10px' }} title={part.name}>{getPartStageAbbreviation(part.name)}</span>
                          </div>
                        </td>
                        <ReadOnlyDateBox columnKey="contractDate" value={pd.contractDate} />
                        <ReadOnlyDateBox columnKey="targetDate" value={pd.targetDate} />
                        <ReadOnlyDateBox columnKey="actualDate" value={pd.actualDate} />
                        <ReadOnlyDateBox columnKey="proformaInvoiceDate" value={pd.proformaInvoiceDate} />
                        <ReadOnlyDateBox columnKey="clientApprovalDate" value={pd.clientApprovalDate} />
                        <ReadOnlyDateBox columnKey="invoiceDate" value={pd.invoiceDate} />
                        <ReadOnlyDateBox columnKey="paidDate" value={pd.paidDate} />
                        <td><span className={`brio-badge ${part.status?.color || ''}`}>{part.status?.label}</span></td>
                      </tr>
                    );
                  })}
                </React.Fragment>
              );
            })}
          </tbody>
        </BrioTable>
      </BrioByCode>
    </div>
  );
}

// ============================================================================
// SUB-COMPONENT: VersionsDropdown
// ============================================================================
// A dropdown shown on the same row as the "Allocated stages only" checkbox.
// First item = "Add version" (saves a snapshot); then "Current (editable)"; then
// each saved version ("V1: <date created>") which opens a read-only view.
function VersionsDropdown({ versions, viewingVersionId, onAdd, onSelect, onReturnToCurrent, onDelete, canManage, saving }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const active = versions.find((v) => v.id === viewingVersionId);
  const triggerLabel = active ? active.label : 'Versions';
  const itemBtn = {
    display: 'flex', alignItems: 'center', gap: '6px', width: '100%', textAlign: 'left',
    background: 'none', border: 'none', cursor: 'pointer', padding: '5px 10px',
    fontSize: '11px', fontFamily: 'Raleway, sans-serif', color: 'var(--brio-text, #333)',
  };

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        title="Timeline versions"
        style={{
          display: 'inline-flex', alignItems: 'center', gap: '5px',
          padding: '3px 8px', fontSize: '10px', fontFamily: 'Raleway, sans-serif',
          border: '1px solid ' + (viewingVersionId ? 'var(--brio-gold)' : 'var(--brio-border)'),
          background: viewingVersionId ? 'var(--brio-gold-light)' : '#fff',
          color: viewingVersionId ? 'var(--brio-gold)' : 'var(--brio-grey)',
          cursor: 'pointer', borderRadius: '2px',
        }}
      >
        <span>{triggerLabel}</span>
        <ChevronDown className="w-3 h-3" />
      </button>
      {open && (
        <div style={{
          position: 'absolute', right: 0, top: 'calc(100% + 4px)',
          minWidth: '200px', maxHeight: '320px', overflowY: 'auto',
          background: '#fff', border: '1px solid var(--brio-border)', borderRadius: '3px',
          boxShadow: '0 4px 14px rgba(0,0,0,0.12)', zIndex: 60, padding: '4px 0',
        }}>
          <button
            type="button"
            style={{ ...itemBtn, color: 'var(--brio-gold)', fontWeight: 600, opacity: saving ? 0.6 : 1 }}
            disabled={saving}
            onClick={() => { setOpen(false); onAdd(); }}
          >
            <Plus className="w-3 h-3" /> {saving ? 'Saving…' : 'Add version'}
          </button>
          <div style={{ height: '1px', background: 'var(--brio-border)', margin: '4px 0' }} />
          <button
            type="button"
            style={{ ...itemBtn, fontWeight: viewingVersionId ? 400 : 600, background: viewingVersionId ? 'none' : 'var(--brio-grey-light)' }}
            onClick={() => { setOpen(false); onReturnToCurrent(); }}
          >
            <Pencil className="w-3 h-3" /> Current (editable)
          </button>
          {versions.length === 0 && (
            <div style={{ padding: '6px 10px', fontSize: '10px', color: 'var(--brio-grey)' }}>No versions yet</div>
          )}
          {versions.map((v) => {
            const isActive = v.id === viewingVersionId;
            return (
              <div key={v.id} style={{ display: 'flex', alignItems: 'center', background: isActive ? 'var(--brio-gold-light)' : 'none' }}>
                <button
                  type="button"
                  style={{ ...itemBtn, flex: 1, fontWeight: isActive ? 600 : 400, color: isActive ? 'var(--brio-gold)' : 'var(--brio-text, #333)' }}
                  onClick={() => { setOpen(false); onSelect(v); }}
                >
                  <Eye className="w-3 h-3" style={{ flexShrink: 0 }} /> {v.label}
                </button>
                {canManage && (
                  <button
                    type="button"
                    title="Delete version"
                    onClick={(e) => { e.stopPropagation(); onDelete(v); }}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px 8px', color: 'var(--brio-grey)', flexShrink: 0 }}
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function TimelinePage({
  // Data
  currentUser,
  activeClients,
  activeProjects,
  activeTasks,
  projects,
  selectedTimelineProject,
  // Functions
  getSupervisedProjects,
  getSortedProjects,
  fmtProject,
  fmtCurr,
  // Supabase
  useSupabaseDB,
  supabaseProjects,
  // Invoice hook — for paid_confirmed locking + invoicePercent source
  invoicesHook,
  // Fee revisions hook — for fee revision banner + invoice creation gate
  feeRevisionsHook,
  // Settings hook — for formatMoney
  settingsHook,
  // State setters
  setSelectedTimelineProject,
  setProjects,
  // Email templates (admin-editable from app_settings)
  customEmailTemplates,
  // Navigation action from RADAR → auto-open invoice pipeline
  pendingTimelineAction,
  clearPendingTimelineAction,
}) {
  const tableProps = getTablePropsFromCode(PRESENTATION_CODE);

  const isAdmin = currentUser.role === 'admin' || currentUser.role === 'admin-pa';
  const isSupervisor = currentUser.role === 'supervisor';
  const canEditProject = isAdmin || isSupervisor;

  // Local state for pending budget allocation edits
  const [pendingAllocations, setPendingAllocations] = useState({});
  const [isSaving, setIsSaving] = useState(false);

  // ── CC expand/collapse state ──
  const [expandedStageKey, setExpandedStageKey] = useState(null);

  // ── Email draft modal state ──
  // For non-invoice fields (contractDate): shows EmailDraftModal before saving.
  // Shape: { stageKey, field, value, stageName, eventType, partId?, oldInvoiceValue? }
  const [emailDraftPending, setEmailDraftPending] = useState(null);

  // ── Invoice workflow state machine ──
  // Replaces the old invoicePipelinePending. Controls a strict sequential flow:
  //   idle → pdf_step → email_step → done
  // Shape when active: {
  //   step: 'pdf_step' | 'email_step',
  //   stageKey, stageName, invoiceDate, invoiceDocType,
  //   field, value, eventType, partId?,
  //   // Populated after PDF step:
  //   invoiceId?, invoiceNumber?, pdfBlob?, pdfFilename?, pdfDownloaded?
  // }
  const [invoiceWorkflow, setInvoiceWorkflow] = useState(null);

  // ── Fee revision gate state ──
  // When a fee revision exists and user tries to create an invoice,
  // we intercept and show RecalculationPreviewModal first.
  const [recalcPreview, setRecalcPreview] = useState(null); // { revision, syntheticInvoice, pendingWorkflow }

  // ── Recommended Next Actions modal context ──
  // When a date change triggers recommended actions, this state opens the
  // intermediate modal. The user must choose an action before any workflow opens.
  // Shape: { triggerType, stageName, stageKey, actions: [...], projectId }
  const [nextActionsContext, setNextActionsContext] = useState(null);

  // NOTE: gateInvoiceWorkflowForFeeRevision and the pendingTimelineAction
  // effect are defined after currentProjectMemo (below) to avoid TDZ errors.

  // ── Help UX state ──
  const [showWorkflowGuide, setShowWorkflowGuide] = useState(false);
  // Context hints: track recently edited fields to show brief hints
  // Shape: { [`${stageKey}:${field}`]: timestamp }
  const [recentEdits, setRecentEdits] = useState({});

  // ── Email-sent confirmation dialog state ──
  // Shape: { stageKey, field, stageName, eventType, partId? }
  const [emailSentConfirmation, setEmailSentConfirmation] = useState(null);

  // ── Communication log (non-blocking audit trail) ──
  const { logEmail } = useCommunicationLog();

  // ── Recommended actions hook ──
  const recActions = useRecommendedActions(
    selectedTimelineProject || (
      (currentUser.role === 'supervisor' ? getSupervisedProjects() : getSortedProjects())?.[0]?.id
    )
  );

  // ── Invoice summary: tracks which stages have paid_confirmed invoices ──
  // Stored WITH the project it was fetched for. Stage keys ('advance', 'td_ad',
  // 'custom_21', …) are shared across every project, so an unlabelled summary
  // would read as valid for whichever project happens to be selected — and the
  // paid-freeze effect below would then persist another project's paid dates
  // onto this one. Consumers must read `invoiceSummary` (the memo further down),
  // which yields {} unless the stored summary belongs to the current project.
  // Shape: { projectId, byStage: { [stageKey]: { hasIssuedInvoice, hasPaidConfirmed, paidDate } } }
  const [invoiceSummaryState, setInvoiceSummaryState] = useState({ projectId: null, byStage: {} });
  // Latest project a summary fetch was started for — lets out-of-order responses be dropped.
  const invoiceSummaryReqRef = useRef(null);

  // ── Timeline versions (read-only snapshots of the timeline table) ──
  // 'billing' keeps these separate from the Timelines (planning) page's versions,
  // which share the same table.
  const timelineVersionsHook = useTimelineVersions('billing');
  // Which saved version (if any) is currently being viewed read-only. null = live/editable view.
  const [viewingVersionId, setViewingVersionId] = useState(null);
  const [savingVersion, setSavingVersion] = useState(false);

  // ── Paid-stage manual-edit unlock ──
  // A stage/part whose invoice status is Paid renders its dates locked (read-only)
  // by default. Clicking the edit (pencil) icon adds its key here to temporarily
  // re-enable manual editing for this session. Keyed by stage key or part key.
  const [unlockedStages, setUnlockedStages] = useState({});
  const toggleStageUnlock = useCallback((key) => {
    setUnlockedStages(prev => ({ ...prev, [key]: !prev[key] }));
  }, []);
  // NOTE: toggleStageSyncLock (the persisted `_syncLocked` toggle) is declared later, right after
  // currentProjectMemo is defined — it depends on it, so it can't live up here (TDZ).

  // Number of columns in the timeline table (Caret + Stage + 7 dates + Status)
  const COLUMN_COUNT = 10;

  // Map default stage IDs (1–16, plus PCD=17) to their timeline storage keys.
  // These keys are used in project.stageTimeline and project.budgetAllocation.
  const DEFAULT_ID_TO_KEY = {
    1: 'advance', 2: 'cd_ad', 3: 'cd_id', 4: 'sd_ad', 5: 'sd_id',
    6: 'dd_ad', 7: 'dd_id', 8: 'aoc_ad', 9: 'aoc_id', 10: 'td_ad', 11: 'td_id',
    12: 'gfc1', 13: 'gfc2', 14: 'gfc3', 15: 'completion', 16: 'cc',
    17: 'pcd', // PCD (Preconcept/Masterplan) — stageId 17 in the default scope template
  };

  // Name-based fallback for tasks whose DB id isn't in DEFAULT_ID_TO_KEY.
  // The contract scope always writes PCD data under the 'pcd' key, but a user's
  // PCD task may have been created with a custom id (≠ 17) via Add Stage. Without
  // this fallback those tasks resolve to `custom_<id>` and the contract/target/etc.
  // dates set on TimelinesPage become invisible here.
  const taskNameToTimelineKey = (name) => {
    const n = String(name || '').trim().toLowerCase();
    if (!n) return null;
    if (n === 'pcd' || n.includes('preconcept') || n.includes('pre-concept') || n.includes('pre concept') || n.includes('masterplan')) return 'pcd';
    return null;
  };

  // Build the stage list dynamically from activeTasks, sorted by order
  const sortedStages = React.useMemo(() => {
    if (!activeTasks || activeTasks.length === 0) {
      // Fallback: if activeTasks not yet loaded, use the 16 defaults
      return Object.entries(DEFAULT_ID_TO_KEY).map(([id, key]) => ({
        key,
        name: key.replace(/_/g, ' ').toUpperCase(),
        id: Number(id),
      }));
    }
    return [...activeTasks]
      .sort((a, b) => {
        const oa = a.order != null ? a.order : (a.id <= 16 ? a.id : 1000 + a.id);
        const ob = b.order != null ? b.order : (b.id <= 16 ? b.id : 1000 + b.id);
        return oa - ob;
      })
      .map(t => ({
        key: DEFAULT_ID_TO_KEY[t.id] || taskNameToTimelineKey(t.name) || `custom_${t.id}`,
        name: t.name,
        id: t.id,
      }));
  }, [activeTasks]);

  // ── "Allocated only" toggle: show only stages with invoicePercent > 0 (CC always visible) ──
  const [showAllocatedOnly, setShowAllocatedOnly] = React.useState(true);

  // ── Inline part-stage rename (zone/scope billing labels like "Main house", "Architecture") ──
  const [editingPartName, setEditingPartName] = useState(null); // { stageKey, partId } | null
  const [partNameValue, setPartNameValue] = useState('');

  // Collapse expanded CC row when project changes
  useEffect(() => {
    setExpandedStageKey(null);
  }, [selectedTimelineProject]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Resolve current project + client for email modal ────────────────
  const availableProjectsMemo = React.useMemo(() => {
    return currentUser.role === 'supervisor' ? getSupervisedProjects() : getSortedProjects();
  }, [currentUser.role, getSupervisedProjects, getSortedProjects]);

  const currentProjectIdMemo = selectedTimelineProject || (availableProjectsMemo.length > 0 ? availableProjectsMemo[0].id : null);
  const currentProjectMemo = availableProjectsMemo.find(p => Number(p.id) === Number(currentProjectIdMemo)) || null;
  const currentClientMemo = currentProjectMemo ? activeClients.find(c => c.id === currentProjectMemo.clientId) || null : null;

  // ── Invoice summary, scoped to the selected project ──────────────────
  // Anything but a summary fetched for the current project reads as empty, so a
  // pending (or stale) fetch can never surface another project's invoice state.
  const invoiceSummary = useMemo(() => (
    currentProjectIdMemo != null && Number(invoiceSummaryState.projectId) === Number(currentProjectIdMemo)
      ? invoiceSummaryState.byStage
      : EMPTY_INVOICE_SUMMARY
  ), [invoiceSummaryState, currentProjectIdMemo]);

  // Fetch the invoice summary for a project and store it under that project id.
  // Responses that arrive after a newer request was started are discarded.
  const refreshInvoiceSummary = useCallback((projId) => {
    if (!invoicesHook?.fetchInvoiceSummaryForProject || !projId) return;
    invoiceSummaryReqRef.current = projId;
    invoicesHook.fetchInvoiceSummaryForProject(projId).then(summary => {
      if (Number(invoiceSummaryReqRef.current) !== Number(projId)) return;
      setInvoiceSummaryState({ projectId: projId, byStage: summary || {} });
    });
  }, [invoicesHook]);

  // Load the invoice summary when the selected project changes (for paid_confirmed locking).
  useEffect(() => {
    refreshInvoiceSummary(currentProjectIdMemo);
  }, [currentProjectIdMemo]); // eslint-disable-line react-hooks/exhaustive-deps

  // Persisted sync-lock: locking a main stage freezes it from the planning-page sync (like a paid stage);
  // unlocking re-enables sync. Stored as `_syncLocked` on the stage's timeline entry so the planning page
  // can read it. Scoped merge — only the toggled stage key changes. (Declared here, after currentProjectMemo.)
  const toggleStageSyncLock = useCallback((key) => {
    if (!currentProjectMemo?.id) return;
    const cur = currentProjectMemo.stageTimeline?.[key] || {};
    const next = { ...cur, _syncLocked: !cur._syncLocked };
    if (useSupabaseDB) supabaseProjects.mergeProjectTimeline(currentProjectMemo.id, { [key]: next }, currentProjectMemo.stageTimeline || {});
    setProjects(prev => prev.map(pr =>
      pr.id === currentProjectMemo.id ? { ...pr, stageTimeline: { ...(pr.stageTimeline || {}), [key]: next } } : pr
    ));
  }, [currentProjectMemo, useSupabaseDB, supabaseProjects, setProjects]);

  // ── Load saved timeline versions when the selected project changes ──
  // Reset any open snapshot view so we never show a stale version for a new project.
  useEffect(() => {
    timelineVersionsHook.fetchVersions(currentProjectIdMemo || null);
    setViewingVersionId(null);
    setUnlockedStages({}); // stage keys are shared across projects — don't carry unlock state over
  }, [currentProjectIdMemo]); // eslint-disable-line react-hooks/exhaustive-deps

  // Versions decorated with user-facing labels ("V1: 12 Aug 2026"), oldest → newest.
  const timelineVersionsForUi = useMemo(() => {
    return (timelineVersionsHook.versions || []).map((v, i) => ({
      id: v.id,
      createdAt: v.createdAt,
      label: `V${i + 1}: ${formatVersionDate(v.createdAt)}`,
      snapshot: v.snapshot,
    }));
  }, [timelineVersionsHook.versions]);

  // The version object currently being viewed (null when on the live/editable table).
  const viewingVersion = useMemo(
    () => timelineVersionsForUi.find(v => v.id === viewingVersionId) || null,
    [timelineVersionsForUi, viewingVersionId]
  );

  // Persist a new read-only snapshot for the current project.
  const saveTimelineVersion = useCallback(async (snapshot) => {
    if (!currentProjectMemo?.id) return;
    setSavingVersion(true);
    try {
      await timelineVersionsHook.saveVersion({
        projectId: currentProjectMemo.id,
        projectName: currentProjectMemo.name || currentProjectMemo.projectName || '',
        snapshot,
      });
    } catch (e) {
      alert('Error saving timeline version: ' + (e?.message || e));
    } finally {
      setSavingVersion(false);
    }
  }, [currentProjectMemo, timelineVersionsHook]);

  // Lookup map for the used-invoice-numbers list inside InvoicePipelineModal
  // (id → project name). Spans all projects so other projects' numbers are
  // labeled too.
  const projectNameLookup = React.useMemo(() => {
    const map = new Map();
    const source = Array.isArray(projects) ? projects : availableProjectsMemo;
    for (const p of source) {
      if (p?.id != null) map.set(Number(p.id), p.name || '');
    }
    return map;
  }, [projects, availableProjectsMemo]);

  // ── Fee revision awareness ──
  // Fetch revisions when the selected project changes so the banner/gate stay current
  useEffect(() => {
    if (currentProjectMemo?.id && feeRevisionsHook?.fetchRevisions) {
      feeRevisionsHook.fetchRevisions(currentProjectMemo.id);
    }
  }, [currentProjectMemo?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const feeRevisions = feeRevisionsHook?.revisions;
  const getLatestRevision = feeRevisionsHook?.getLatestRevision;
  const latestRevision = useMemo(() => {
    return getLatestRevision?.() || null;
  }, [feeRevisions, getLatestRevision]);

  // ── Fee revision gate: intercept invoice workflow when a revision exists ──
  // Returns true if gated (modal shown), false if workflow should proceed normally.
  // Defined here (after currentProjectMemo) to avoid const TDZ errors.
  const gateInvoiceWorkflowForFeeRevision = useCallback((workflow) => {
    const revision = feeRevisionsHook?.getLatestRevision?.();
    if (!revision || !workflow.stageKey || !currentProjectMemo) return false;

    const totalFee = getCurrentTotalFee(currentProjectMemo);
    const sk = workflow.stageKey || '';

    // Compute suggested base amount using the same logic as InvoicePipelineModal
    let suggestedBase = 0;
    const partIdx = sk.indexOf('__part__');
    if (partIdx > -1) {
      // Part stage: use part's billing (lumpsum or percentOfStage of parent)
      const parentKey = sk.slice(0, partIdx);
      const partId = sk.slice(partIdx + '__part__'.length);
      const partStages = currentProjectMemo.stageTimeline?.[parentKey]?.partStages;
      const part = Array.isArray(partStages)
        ? partStages.find(ps => String(ps.id) === String(partId))
        : null;
      const billing = part?.billing || {};
      const lumpsum = Number(billing.lumpsum ?? billing.lumpSum ?? 0);
      if (lumpsum > 0) {
        suggestedBase = lumpsum;
      } else {
        const parentBilling = currentProjectMemo.stageTimeline?.[parentKey]?.billing || {};
        const parentLumpsum = Number(parentBilling.lumpsum ?? parentBilling.lumpSum ?? 0);
        const parentPlanned = parentLumpsum > 0
          ? parentLumpsum
          : totalFee * (Number(parentBilling.percent) || 0) / 100;
        const pctOfStage = Number(billing.percentOfStage) || 0;
        suggestedBase = parentPlanned * pctOfStage / 100;
      }
    } else if (sk.startsWith('cc__') && sk !== 'cc') {
      // CC monthly
      const billing = currentProjectMemo.stageTimeline?.[sk]?.billing || {};
      const lumpsum = Number(billing.lumpsum ?? billing.lumpSum ?? 0);
      suggestedBase = lumpsum > 0 ? lumpsum : (Number(currentProjectMemo.retainerConfig?.monthlyAmount) || 0);
    } else if (sk.startsWith('misc__')) {
      // Misc scope
      const scopeId = sk.replace('misc__', '');
      const scope = (Array.isArray(currentProjectMemo.miscFeeScopes) ? currentProjectMemo.miscFeeScopes : [])
        .find(s => s.id === scopeId);
      const billing = currentProjectMemo.stageTimeline?.[sk]?.billing || {};
      const lumpsum = Number(billing.lumpsum ?? billing.lumpSum ?? 0);
      suggestedBase = lumpsum > 0 ? lumpsum : (Number(scope?.amount) || 0);
    } else {
      // Full stage: use billing lumpsum or percent of total fee
      const billing = currentProjectMemo.stageTimeline?.[sk]?.billing || {};
      const lumpsum = Number(billing.lumpsum ?? billing.lumpSum ?? 0);
      if (lumpsum > 0) {
        suggestedBase = lumpsum;
      } else {
        const percent = Number(billing.percent) || 0;
        suggestedBase = totalFee * percent / 100;
      }
    }

    setRecalcPreview({
      revision,
      syntheticInvoice: {
        stageKey: workflow.stageKey,
        baseAmount: suggestedBase,
        gstPercent: 18,
        tdsPercent: 10,
        isManualOverride: false,
      },
      pendingWorkflow: workflow,
    });
    return true;
  }, [getLatestRevision, currentProjectMemo]);

  // ── Consume pending timeline action from RADAR navigation ──
  useEffect(() => {
    if (!pendingTimelineAction?.openInvoicePipeline) return;
    const { stageKey, stageName, invoiceDate, invoiceDocType } = pendingTimelineAction;
    if (!stageKey) return;
    const field = invoiceDocType === 'proforma' ? 'proformaInvoiceDate' : 'invoiceDate';
    const eventType = invoiceDocType === 'proforma' ? 'proforma_invoice_admin' : 'tax_invoice_admin';
    const workflow = {
      step: 'pdf_step',
      stageKey,
      stageName: stageName || stageKey,
      invoiceDate: invoiceDate || new Date().toISOString().slice(0, 10),
      invoiceDocType: invoiceDocType || 'tax',
      field,
      value: invoiceDate || new Date().toISOString().slice(0, 10),
      eventType,
    };
    if (!gateInvoiceWorkflowForFeeRevision(workflow)) {
      setInvoiceWorkflow(workflow);
    }
    if (clearPendingTimelineAction) clearPendingTimelineAction();
  }, [pendingTimelineAction, clearPendingTimelineAction, gateInvoiceWorkflowForFeeRevision]);

  // ── Consume closure→invoice date redirect from TimelinesPage ──
  // Waits for the project to load, then calls handleDateChangeWithEmail so the
  // invoice date is saved and the "Recommended Next Action" modal fires normally.
  useEffect(() => {
    if (!pendingTimelineAction?.triggerInvoiceDate) return;
    if (!currentProjectMemo) return; // wait for project to load
    const { stageKey, stageName, invoiceDate } = pendingTimelineAction;
    if (!stageKey || !invoiceDate) return;
    handleDateChangeWithEmail(stageKey, 'invoiceDate', invoiceDate, stageName || stageKey);
    if (clearPendingTimelineAction) clearPendingTimelineAction();
  }, [pendingTimelineAction, currentProjectMemo]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Consume trigger context from TimelinesPage "Add to tracking" → "Go to Tracking" ──
  // Fires the Recommended Next Actions modal exactly as if the user had typed the date here.
  useEffect(() => {
    if (!pendingTimelineAction?.triggerRecommendedAction) return;
    if (!currentProjectMemo) return; // wait for project to load
    const { triggerType, stageKey, stageName, partId, trackingField, dateStr, prevValue } = pendingTimelineAction;
    if (!triggerType || !stageKey) return;
    if (clearPendingTimelineAction) clearPendingTimelineAction();
    const routing = getTriggerRouting(triggerType);
    const meta = {
      stageKey,
      stageName: stageName || stageKey,
      projectName: currentProjectMemo?.name || currentProjectMemo?.projectName || '',
      projectCode: currentProjectMemo?.projectCode || '',
      partId: partId || null,
      routing,
      // Field-specific metadata matching what handleDateChangeWithEmail would build
      ...(trackingField === 'actualDate' ? { date: dateStr } : {}),
      ...(trackingField === 'targetDate' ? { previousDate: prevValue, revisedDate: dateStr } : {}),
    };
    recActions.generateFromTrigger(triggerType, meta).then(created => {
      if (created.length > 0) {
        setNextActionsContext({ triggerType, stageName: meta.stageName, stageKey, actions: created, projectId: currentProjectMemo?.id, routing });
      }
    });
  }, [pendingTimelineAction, currentProjectMemo]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Scan for overdue invoices and missing payment terms on project load ──
  useEffect(() => {
    if (!currentProjectMemo || !invoicesHook?.invoices) return;
    const projectInvoices = invoicesHook.invoices.filter(
      inv => Number(inv.projectId) === Number(currentProjectMemo.id)
    );
    if (projectInvoices.length > 0 || currentClientMemo) {
      recActions.scanProjectForActions(currentProjectMemo, projectInvoices, currentClientMemo);
    }
  }, [currentProjectMemo?.id, invoicesHook?.invoices?.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Date-change workflow trigger system ──────────────────────────
  // Uses resolveTimelineTrigger() from timelineTriggerEngine.js to decide
  // which date changes are passive vs active. All trigger decisions are
  // centralized in that module — no trigger logic lives in this file.

  // ── Helper: build a new timeline with a date field set (pure, no side effects) ──
  const buildTimelineWithDate = useCallback((stageKey, field, value, opts = {}) => {
    if (opts.partId) {
      const parentTimeline = currentProjectMemo?.stageTimeline?.[stageKey] || {};
      const parts = (parentTimeline.partStages || []).map(part =>
        part.id === opts.partId ? { ...part, [field]: value || null } : part
      );
      return {
        ...currentProjectMemo?.stageTimeline,
        [stageKey]: { ...parentTimeline, partStages: parts },
      };
    }
    const baseTimeline = {
      ...currentProjectMemo?.stageTimeline,
      [stageKey]: {
        ...(currentProjectMemo?.stageTimeline?.[stageKey] || {}),
        [field]: value || null,
      },
    };
    // When any billing-driving field changes (Appr. / invoice / paid / actual), recompute the derived
    // Invoicing and Closure system-row dates from the stage's resulting values via the shared helper:
    //   invoicing start = Appr. date (else invoice date); end = paid date once paid, else start + terms
    //   closure         = paid on time → actual; paid late → paid; unpaid past the window → today
    if (field === 'clientApprovalDate' || field === 'invoiceDate' || field === 'paidDate' || field === 'actualDate') {
      const stageNow = baseTimeline[stageKey] || {};
      const invoicingKey = `invoicing_${stageKey}`;
      const closureKey = `closure_${stageKey}`;
      const invExisting = baseTimeline[invoicingKey] || {};
      const closeExisting = baseTimeline[closureKey] || {};
      const dur = invExisting.plannedDuration || currentClientMemo?.paymentTermsDays || null;
      const { invoicingStart, invoicingEnd, closureDate } = deriveRowDates(stageNow, dur);
      // Write (or clear) a derived row only when it has or had dates, so stages without one don't gain
      // empty keys.
      if (invoicingStart || invExisting.plannedStartDate || invExisting.plannedEndDate) {
        baseTimeline[invoicingKey] = { ...invExisting, plannedStartDate: invoicingStart || null, plannedEndDate: invoicingEnd || null };
      }
      // "Ignore delay" on the planning page snaps the closure to the actual date; keep honouring it.
      const closeSnap = closureDate ? (closeExisting._delayIgnored ? (stageNow.actualDate || null) : closureDate) : null;
      if (closeSnap || closeExisting.plannedStartDate || closeExisting.plannedEndDate) {
        baseTimeline[closureKey] = { ...closeExisting, plannedStartDate: closeSnap, plannedEndDate: closeSnap };
      }
    }

    return baseTimeline;
  }, [currentProjectMemo, currentClientMemo]);

  // ── Helper: persist a timeline object to DB + local state ──
  const persistTimeline = useCallback((newTimeline) => {
    if (useSupabaseDB) {
      // Only send keys whose object reference changed (unchanged keys share refs via spread).
      const old = currentProjectMemo?.stageTimeline || {};
      const partial = {};
      for (const k of Object.keys(newTimeline)) {
        if (newTimeline[k] !== old[k]) partial[k] = newTimeline[k];
      }
      if (Object.keys(partial).length > 0) {
        supabaseProjects.mergeProjectTimeline(currentProjectMemo.id, partial, currentProjectMemo.stageTimeline || {});
      }
    }
    setProjects(prev => prev.map(pr =>
      pr.id === currentProjectMemo?.id ? { ...pr, stageTimeline: newTimeline } : pr
    ));
  }, [currentProjectMemo, useSupabaseDB, supabaseProjects, setProjects]);

  // ── Paid date from the Invoices page wins.
  // The planning page can't see invoice status, so it recognises "paid" (and derives the Invoicing /
  // Closure rows) from the recorded paidDate. For any stage/part that is invoice-confirmed paid, write
  // the invoice's paid date into the timeline whenever the recorded one is missing OR different (a date
  // typed in when the invoice went out is otherwise never corrected), and re-derive that stage's
  // Invoicing / Closure rows from it. Settles after one write: the next pass finds them equal.
  useEffect(() => {
    if (!currentProjectMemo?.id) return;
    const tl = currentProjectMemo.stageTimeline || {};
    const stageKeys = new Set([
      ...sortedStages.filter(s => s.key !== 'cc').map(s => s.key),
      ...Object.keys(tl).filter(k => k.startsWith('misc__')),
    ]);
    const scoped = {};
    stageKeys.forEach((key) => { if (tl[key] && typeof tl[key] === 'object') scoped[key] = tl[key]; });
    const corrected = applyInvoicePaidDates(scoped, invoiceSummary);
    if (corrected === scoped) return;
    const terms = currentClientMemo?.paymentTermsDays || null;
    const partial = {};
    // Re-derive one Invoicing / Closure pair from a (corrected) stage or part record.
    const rederive = (rec, invKey, closeKey) => {
      const invEx = partial[invKey] || tl[invKey] || {};
      const closeEx = partial[closeKey] || tl[closeKey] || {};
      const { invoicingStart, invoicingEnd, closureDate } = deriveRowDates(rec, invEx.plannedDuration || terms);
      if (invoicingStart) partial[invKey] = { ...invEx, plannedStartDate: invoicingStart, plannedEndDate: invoicingEnd };
      if (closureDate) {
        // "Ignore delay" on the planning page snaps the closure to the actual date; keep honouring it.
        const snapped = closeEx._delayIgnored ? (rec.actualDate || null) : closureDate;
        partial[closeKey] = { ...closeEx, plannedStartDate: snapped, plannedEndDate: snapped };
      }
    };
    Object.keys(corrected).forEach((key) => {
      if (corrected[key] === scoped[key]) return;
      partial[key] = corrected[key];
      if (corrected[key].paidDate !== scoped[key].paidDate) rederive(corrected[key], `invoicing_${key}`, `closure_${key}`);
      const oldParts = scoped[key].partStages || [];
      (corrected[key].partStages || []).forEach((p, i) => {
        if (p === oldParts[i]) return;
        const subKey = `substage_${key}_${p.id}`;
        rederive(p, `invoicing_${subKey}`, `closure_${subKey}`);
      });
    });
    if (useSupabaseDB) {
      supabaseProjects.mergeProjectTimeline(currentProjectMemo.id, partial, currentProjectMemo.stageTimeline || {});
    }
    setProjects(prev => prev.map(pr =>
      pr.id === currentProjectMemo.id
        ? { ...pr, stageTimeline: { ...(pr.stageTimeline || {}), ...partial } }
        : pr
    ));
  }, [currentProjectMemo?.id, invoiceSummary, sortedStages]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Helper: save a timeline date (used by standalone date handlers) ──
  const saveTimelineDate = useCallback((stageKey, field, value, opts = {}) => {
    const newTimeline = buildTimelineWithDate(stageKey, field, value, opts);
    persistTimeline(newTimeline);
    return newTimeline;
  }, [buildTimelineWithDate, persistTimeline]);

  // ── Email status helpers (delegating to timelineTriggerEngine) ───
  // Read the stored email status for a stage/field from stageTimeline data.
  const getStoredEmailStatus = useCallback((stageKey, field, opts = {}) => {
    const tl = currentProjectMemo?.stageTimeline || {};
    if (opts.partId) {
      const parts = tl[stageKey]?.partStages || [];
      const part = parts.find(p => p.id === opts.partId);
      return part?.emailStatus?.[field] || null;
    }
    return tl[stageKey]?.emailStatus?.[field] || null;
  }, [currentProjectMemo]);

  // Derive the display email status for a field using the central engine.
  const deriveEmailStatus = useCallback((stageKey, field, dateValue, opts = {}) => {
    const storedStatus = getStoredEmailStatus(stageKey, field, opts);
    return deriveEmailStatusFromField({ fieldKey: field, dateValue, storedStatus });
  }, [getStoredEmailStatus]);

  // Persist email status change to stageTimeline
  const updateEmailStatus = useCallback((stageKey, field, newStatus, opts = {}) => {
    if (!currentProjectMemo) return;
    const tl = { ...(currentProjectMemo.stageTimeline || {}) };
    if (opts.partId) {
      const parentTl = { ...(tl[stageKey] || {}) };
      const parts = (parentTl.partStages || []).map(part => {
        if (part.id !== opts.partId) return part;
        return { ...part, emailStatus: { ...(part.emailStatus || {}), [field]: newStatus } };
      });
      parentTl.partStages = parts;
      tl[stageKey] = parentTl;
    } else {
      tl[stageKey] = {
        ...(tl[stageKey] || {}),
        emailStatus: { ...(tl[stageKey]?.emailStatus || {}), [field]: newStatus },
      };
    }
    if (useSupabaseDB) {
      // Only stageKey changed; send it alone.
      supabaseProjects.mergeProjectTimeline(currentProjectMemo.id, { [stageKey]: tl[stageKey] }, currentProjectMemo.stageTimeline || {});
    }
    setProjects(prev => prev.map(pr =>
      pr.id === currentProjectMemo.id ? { ...pr, stageTimeline: tl } : pr
    ));
  }, [currentProjectMemo, useSupabaseDB, supabaseProjects, setProjects]);

  // Central date-change handler: saves the date, updates email markers,
  // and triggers workflows ONLY for the correct active actions (see trigger matrix).
  const handleDateChangeWithEmail = useCallback((stageKey, field, value, stageName, opts = {}) => {
    // Freeze is governed by the pen/lock now, not by whether a stage has part-stages: a SYNC-LOCKED main
    // stage's own date cells are read-only (frozen like paid) — block any parent edit on it. An unlocked
    // stage (with or without parts) is freely editable, and the planning page only syncs to it while
    // unlocked. Part edits (opts.partId) are always allowed.
    if (!opts.partId && currentProjectMemo?.stageTimeline?.[stageKey]?._syncLocked) return;
    // Read previous value BEFORE saving
    const tl = currentProjectMemo?.stageTimeline || {};
    let previousValue;
    if (opts.partId) {
      const parts = tl[stageKey]?.partStages || [];
      const part = parts.find(p => p.id === opts.partId);
      previousValue = part?.[field] || null;
    } else {
      previousValue = tl[stageKey]?.[field] || null;
    }

    // Build the new timeline with the date change
    let newTimeline = buildTimelineWithDate(stageKey, field, value, opts);

    // Merge email status update into the SAME timeline object to avoid
    // a race condition where two separate updateProject calls overwrite each other
    if (isFieldActive(field)) {
      let newEmailStatus; // undefined = no change needed
      if (!value) {
        newEmailStatus = null; // clear email status when date is cleared
      } else {
        const storedStatus = getStoredEmailStatus(stageKey, field, opts);
        if (!storedStatus || storedStatus === 'not_applicable') {
          newEmailStatus = 'pending';
        }
        // else: keep existing status, don't overwrite
      }
      // Only modify if we have a status change to apply
      if (newEmailStatus !== undefined) {
        if (opts.partId) {
          const parentTl = { ...(newTimeline[stageKey] || {}) };
          const parts = (parentTl.partStages || []).map(part => {
            if (part.id !== opts.partId) return part;
            const es = { ...(part.emailStatus || {}) };
            if (newEmailStatus === null) { delete es[field]; } else { es[field] = newEmailStatus; }
            return { ...part, emailStatus: es };
          });
          parentTl.partStages = parts;
          newTimeline = { ...newTimeline, [stageKey]: parentTl };
        } else {
          const stageEntry = { ...(newTimeline[stageKey] || {}) };
          const es = { ...(stageEntry.emailStatus || {}) };
          if (newEmailStatus === null) { delete es[field]; } else { es[field] = newEmailStatus; }
          stageEntry.emailStatus = es;
          newTimeline = { ...newTimeline, [stageKey]: stageEntry };
        }
      }
    }

    // Single persist: date + email status in one write
    persistTimeline(newTimeline);

    // Show context hint for key fields when a value is entered
    if (value && CONTEXT_HINTS[field] && !previousValue) {
      const hintKey = `${stageKey}:${field}`;
      setRecentEdits(prev => ({ ...prev, [hintKey]: Date.now() }));
      setTimeout(() => {
        setRecentEdits(prev => {
          const next = { ...prev };
          delete next[hintKey];
          return next;
        });
      }, 4000);
    }

    // Determine workflow trigger via central engine
    // Pass planned date threshold for targetDate trigger gating
    const plannedDateThreshold = (currentClientMemo?.plannedDateTriggerThresholdDays ?? currentProjectMemo?.plannedDateTriggerThresholdDays ?? 7);
    const triggerType = resolveTimelineTrigger({
      fieldKey: field,
      previousValue,
      nextValue: value || null,
      stageKey,
      plannedDateThreshold,
    });

    if (triggerType) {
      const routing = getTriggerRouting(triggerType);

      // Generate recommended actions and show the intermediate modal
      const meta = {
        stageKey,
        stageName,
        projectName: currentProjectMemo?.name || currentProjectMemo?.projectName || '',
        projectCode: currentProjectMemo?.projectCode || '',
        partId: opts.partId || null,
        routing, // pass routing info to action generation
        ...(field === 'actualDate' ? { date: value } : {}),
        ...(field === 'proformaInvoiceDate' ? { date: value } : {}),
        ...(field === 'invoiceDate' ? { invoiceDate: value, oldInvoiceValue: opts.oldInvoiceValue } : {}),
        ...(field === 'targetDate' ? { previousDate: previousValue, revisedDate: value } : {}),
        ...(field === 'paidDate' ? { date: value } : {}),
      };
      recActions.generateFromTrigger(triggerType, meta).then(created => {
        if (created.length > 0) {
          setNextActionsContext({
            triggerType,
            stageName,
            stageKey,
            actions: created,
            projectId: currentProjectMemo?.id,
            routing, // pass routing for UI to decide behavior
          });
        }
      });
    }
  }, [buildTimelineWithDate, persistTimeline, getStoredEmailStatus, currentProjectMemo, recActions]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Launch a recommended action's workflow ──────────────────────────
  const handleLaunchAction = useCallback((action) => {
    const meta = action.metadata || {};
    const baseStageKey = action.stageKey || meta.stageKey;
    const stageName = meta.stageName || baseStageKey || '';
    const actionRouting = meta.routing || null;
    // Build composite stageKey for part stages (parentKey__part__partId)
    const stageKey = (baseStageKey && meta.partId)
      ? `${baseStageKey}__part__${meta.partId}`
      : baseStageKey;

    switch (action.actionType) {
      case 'create_proforma_invoice':
        // Proforma invoice creation: open pipeline for PDF generation + email
        if (stageKey) {
          const proformaWf = {
            step: 'pdf_step',
            stageKey,
            stageName,
            invoiceDate: meta.invoiceDate || new Date().toISOString().slice(0, 10),
            invoiceDocType: 'proforma',
            field: 'proformaInvoiceDate',
            value: meta.invoiceDate || new Date().toISOString().slice(0, 10),
            eventType: 'proforma_invoice_admin',
          };
          if (!gateInvoiceWorkflowForFeeRevision(proformaWf)) {
            setInvoiceWorkflow(proformaWf);
          }
          recActions.complete(action.id);
        }
        break;

      case 'create_tax_invoice':
        // Tax invoice creation: open pipeline for PDF generation + email
        if (stageKey) {
          const taxWf = {
            step: 'pdf_step',
            stageKey,
            stageName,
            invoiceDate: meta.invoiceDate || new Date().toISOString().slice(0, 10),
            invoiceDocType: 'tax',
            field: 'invoiceDate',
            value: meta.invoiceDate || new Date().toISOString().slice(0, 10),
            eventType: 'tax_invoice_admin',
          };
          if (!gateInvoiceWorkflowForFeeRevision(taxWf)) {
            setInvoiceWorkflow(taxWf);
          }
          recActions.complete(action.id);
        }
        break;

      // Stage completion approval (native mail — project-stage)
      case 'draft_stage_completion_approval_email': {
        const triggerField = 'actualDate';
        if (stageKey) {
          updateEmailStatus(stageKey, triggerField, 'draft_opened', { partId: meta.partId || null });
        }
        setEmailDraftPending({
          stageKey: stageKey || '',
          field: triggerField,
          value: new Date().toISOString().slice(0, 10),
          stageName,
          eventType: 'stage_completion_approval',
          partId: meta.partId || null,
          oldInvoiceValue: null,
        });
        recActions.complete(action.id);
        break;
      }

      // Payment confirmed (native mail — project-stage)
      case 'draft_payment_confirmed_email': {
        const triggerField = 'paidDate';
        if (stageKey) {
          updateEmailStatus(stageKey, triggerField, 'draft_opened', { partId: meta.partId || null });
        }
        setEmailDraftPending({
          stageKey: stageKey || '',
          field: triggerField,
          value: new Date().toISOString().slice(0, 10),
          stageName,
          eventType: 'payment_confirmed',
          partId: meta.partId || null,
          oldInvoiceValue: null,
        });
        recActions.complete(action.id);
        break;
      }

      // Planned date revised (native mail — project-stage)
      case 'draft_planned_date_revised_email': {
        setEmailDraftPending({
          stageKey: stageKey || '',
          field: 'targetDate',
          value: new Date().toISOString().slice(0, 10),
          stageName,
          eventType: 'planned_date_revised',
          partId: meta.partId || null,
          oldInvoiceValue: null,
          extraVars: { previousDate: meta.previousDate, revisedDate: meta.revisedDate },
        });
        recActions.complete(action.id);
        break;
      }

      case 'draft_project_initiated_email':
      case 'draft_stage_initiation_email':
      case 'draft_stage_closure_email':
      case 'draft_tax_invoice_email':
      case 'draft_timeline_change_email':
      case 'draft_payment_reminder_email': {
        const eventType = ACTION_EVENT_TYPE_MAP[action.actionType] || 'invoice_sent';
        // Map action type → trigger field for email status tracking
        const triggerField = ({
          draft_project_initiated_email: 'paidDate',
          draft_stage_initiation_email: 'paidDate',
          draft_stage_closure_email: 'actualDate',
          draft_tax_invoice_email: 'invoiceDate',
        })[action.actionType] || null;
        // Mark email status as draft_opened for the relevant field
        if (triggerField && stageKey) {
          updateEmailStatus(stageKey, triggerField, 'draft_opened', { partId: meta.partId || null });
        }
        setEmailDraftPending({
          stageKey: stageKey || '',
          field: triggerField || '',
          value: new Date().toISOString().slice(0, 10),
          stageName,
          eventType,
          partId: meta.partId || null,
          oldInvoiceValue: null,
          // Pass metadata for timeline change emails
          ...(action.actionType === 'draft_timeline_change_email' && meta.previousDate ? {
            extraVars: { previousDate: meta.previousDate, revisedDate: meta.revisedDate },
          } : {}),
        });
        recActions.complete(action.id);
        break;
      }

      case 'review_missing_payment_terms':
        alert('Action: Please set payment terms for this client in the Clients page before invoicing.');
        recActions.dismiss(action.id);
        break;

      default:
        recActions.dismiss(action.id);
        break;
    }
  }, [setInvoiceWorkflow, setEmailDraftPending, recActions]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Handle action selection from the Recommended Next Actions modal ──
  const handleNextActionSelect = useCallback((action) => {
    setNextActionsContext(null); // Close the intermediate modal
    handleLaunchAction(action);  // Launch the chosen workflow
  }, [handleLaunchAction]);

  const handleNextActionSkip = useCallback(() => {
    setNextActionsContext(null); // Just close
  }, []);

  return (
    <div className="brio-flex brio-flex-col brio-analytics-font">
      {/* CSS to hide date picker calendar icon and number spinners */}
      <style>{`
        .timeline-date-input::-webkit-calendar-picker-indicator {
          display: none;
          -webkit-appearance: none;
        }
        .timeline-date-input::-webkit-inner-spin-button,
        .timeline-date-input::-webkit-clear-button {
          display: none;
          -webkit-appearance: none;
        }
        .timeline-date-input {
          -moz-appearance: textfield;
        }
        .timeline-percent-input::-webkit-inner-spin-button,
        .timeline-percent-input::-webkit-outer-spin-button {
          -webkit-appearance: none;
          margin: 0;
        }
        .timeline-percent-input {
          -moz-appearance: textfield;
        }
        @keyframes brio-hint-fade {
          0%   { opacity: 0; transform: translateY(-2px); }
          10%  { opacity: 1; transform: translateY(0); }
          80%  { opacity: 1; }
          100% { opacity: 0; }
        }
        /* Column header info tooltip */
        .tl-info-tip {
          display: none;
          position: absolute;
          top: 100%;
          left: 50%;
          transform: translateX(-50%);
          margin-top: 4px;
          padding: 5px 8px;
          background: var(--brio-black, #111);
          color: var(--brio-white, #fff);
          font-size: 9px;
          font-weight: 400;
          line-height: 1.4;
          white-space: normal;
          width: 160px;
          z-index: 200;
          pointer-events: none;
          border-radius: 3px;
          box-shadow: 0 2px 6px rgba(0,0,0,0.18);
          font-family: var(--brio-font-family);
        }
        .tl-info-trigger:hover .tl-info-tip {
          display: block;
        }
        .tl-info-trigger:hover {
          opacity: 1 !important;
        }
      `}</style>

      {/* PAGE HEADER BAND: Full-width white background, centered content */}
      <div className="brio-pageband brio-pageband--header">
        <BrioFrame>
          <div className="brio-page-header__content">
            <div className="brio-page-header__left" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <p className="brio-analytics-muted brio-mt-xs">Track stage completion, client approvals, and invoice payments</p>
              <button
                onClick={() => setShowWorkflowGuide(true)}
                title="Timeline workflow guide"
                style={{
                  background: 'none', border: '1px solid var(--brio-border)',
                  borderRadius: '50%', width: '20px', height: '20px',
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  cursor: 'pointer', flexShrink: 0, color: 'var(--brio-grey)',
                  marginTop: '2px',
                }}
              >
                <HelpCircle className="w-3 h-3" />
              </button>
            </div>
          </div>
        </BrioFrame>
      </div>

      {/* CONTENT AREA: Wrapped in BrioFrame for 875px alignment */}
      <BrioFrame>
        {/* Fee Revision Banner — shown when a revision exists for the current project */}
        {latestRevision && currentProjectMemo && (() => {
          const fmtCC = settingsHook?.formatMoney
            ? (a) => settingsHook.formatMoney(a, currentProjectMemo.contractCurrency || 'INR')
            : fmtCurr;
          return (
            <div style={{ padding: '10px 12px', marginTop: 'var(--brio-space-s)', background: 'var(--brio-gold-light)', border: '1px solid var(--brio-gold, #9F902A)', display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
              <AlertTriangle className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--brio-gold, #9F902A)', marginTop: '2px' }} />
              <div style={{ flex: 1 }}>
                <div className="brio-text-s brio-font-bold" style={{ marginBottom: '2px' }}>Fee Revision Active</div>
                <div className="brio-text-xs" style={{ lineHeight: '1.5' }}>
                  The project fee changed from {fmtCC(latestRevision.oldTotalFee)} to {fmtCC(latestRevision.newTotalFee)}{' '}
                  ({latestRevision.deltaFee >= 0 ? '+' : ''}{fmtCC(latestRevision.deltaFee)})
                  {latestRevision.effectiveDate ? `, effective ${latestRevision.effectiveDate}` : ''}.
                  {latestRevision.reason ? ` Reason: ${latestRevision.reason}` : ''}
                </div>
                <div className="brio-text-xs brio-text-grey" style={{ marginTop: '4px' }}>
                  Any new invoice created from this page will show an adjustment preview before proceeding.
                </div>
              </div>
            </div>
          );
        })()}
        <div className="brio-gap-s" style={{ paddingTop: 'var(--brio-space-s)' }}>
          <BrioAnalyticsSurface padding="m">
            {(() => {
              // Get available projects based on role
              const availableProjects = currentUser.role === 'supervisor' ? getSupervisedProjects() : getSortedProjects();
              
              // Check if there are any projects
              if (!availableProjects || availableProjects.length === 0) {
                return (
                  <div className="brio-text-center brio-analytics-muted" style={{ padding: '48px 0' }}>
                    <p className="brio-mb-s">No projects available</p>
                    {currentUser.role === 'supervisor' && (
                      <p className="brio-text-xs">You are not assigned to supervise any projects. Contact an admin to assign projects to you.</p>
                    )}
                  </div>
                );
              }

              // Get the current project ID (selected or default to first)
              const defaultProjectId = availableProjects[0].id;
              const currentProjectId = selectedTimelineProject || defaultProjectId;
              
              // Find the actual project
              const project = availableProjects.find(p => Number(p.id) === Number(currentProjectId));

              // ── Proforma column cell renderer ────────────────────────────────
              // Surfaces proforma invoice dates recorded on the Invoices page
              // (via the invoice summary, keyed by doc type) so existing proforma
              // invoices show up here — for main stages AND substages — while still
              // letting an admin/supervisor set or override a manual proforma date.
              // Mirrors the Invoice column's "source of truth from Invoices page"
              // read-only treatment for non-editors.
              //   summaryEntry — invoiceSummary[...] for this stage/part (already resolved)
              //   timelineObj  — the stage timeline object or the part object
              //   stageKey     — parent stage key used by the date/email handlers
              //   opts         — { partId } for substage rows, {} for stage rows
              const renderProformaCell = (summaryEntry, timelineObj, stageKey, displayName, opts = {}) => {
                const issuedProformaDate = summaryEntry?.lastIssuedProformaDate || null;
                const derivedDate = issuedProformaDate || summaryEntry?.lastProformaInvoiceDate || null;
                const manualDate = timelineObj?.proformaInvoiceDate || '';
                // Paid stage locked (not unlocked for manual edit): render a static box.
                if (opts.locked) {
                  return <LockedDateBox value={manualDate || derivedDate || ''} />;
                }
                // Non-editors: show the issued proforma date as a locked source of truth.
                if (issuedProformaDate && !canEditProject) {
                  const p = issuedProformaDate.split('-');
                  const formatted = p.length === 3 ? `${p[0]}-${p[1]}-${p[2]}` : issuedProformaDate;
                  return (
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <div title="Proforma invoice date from Invoices page" style={{
                        width: '100%', height: '22px', boxSizing: 'border-box', border: '1px solid var(--brio-border)', padding: '0 4px',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: '9px', fontFamily: 'var(--brio-font-family)',
                        background: 'var(--brio-gold-light, #fef9e7)', color: 'var(--brio-gold, #9F902A)',
                      }}>{formatted}</div>
                      <EmailStatusMarker
                        status={deriveEmailStatus(stageKey, 'proformaInvoiceDate', issuedProformaDate, opts)}
                        onChange={(s) => updateEmailStatus(stageKey, 'proformaInvoiceDate', s, opts)} />
                    </div>
                  );
                }
                // Editors (and everyone before a proforma invoice exists): editable
                // picker. A manual override wins; otherwise pre-fill from the
                // proforma invoice so the date is visible and can be adjusted.
                const inputValue = manualDate || derivedDate || '';
                return (
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <CompactDateInput
                      value={inputValue}
                      onChange={(e) => {
                        handleDateChangeWithEmail(stageKey, 'proformaInvoiceDate', e.target.value, displayName, opts);
                      }} />
                    <EmailStatusMarker
                      status={deriveEmailStatus(stageKey, 'proformaInvoiceDate', inputValue, opts)}
                      onChange={(s) => updateEmailStatus(stageKey, 'proformaInvoiceDate', s, opts)} />
                  </div>
                );
              };

              return (
                <>
                  {/* Project Selection Dropdown */}
                  <div className="brio-mb-s">
                    <div className="brio-flex brio-justify-between brio-items-center brio-mb-xs">
                      <label className="brio-text-m brio-font-medium">Select Project:</label>
                      <span className="brio-text-xs brio-text-grey">
                        {availableProjects.length} {availableProjects.length === 1 ? 'project' : 'projects'}
                      </span>
                    </div>
                    <select
                      value={currentProjectId}
                      onChange={(e) => {
                        const newId = Number(e.target.value);
                        setSelectedTimelineProject(newId);
                        setPendingAllocations({});
                      }}
                      className="brio-input"
                      style={{ width: '100%', maxWidth: '384px', padding: '8px 12px', border: '1px solid var(--brio-border)', fontFamily: 'var(--brio-font-family)' }}
                    >
                      {availableProjects.map(p => {
                        const c = activeClients.find(x => x.id === p.clientId);
                        return (
                          <option key={p.id} value={p.id}>
                            {fmtProject(p)} {c ? `- ${c?.organisation || c?.name}` : ''}
                          </option>
                        );
                      })}
                    </select>
                  </div>

                  {/* Project Details and Table */}
                  {!project ? (
                    <BrioAlert label="Debug Info">
                      Project Not Found - Current Project ID: {currentProjectId}
                    </BrioAlert>
                  ) : (
                    <>
                      {(() => {
                        const client = activeClients.find(c => c.id === project.clientId);
                        
                        // Initialize timeline tracking if not exists
                        if (!project.stageTimeline) {
                          project.stageTimeline = {};
                        }

                        // Use the dynamically-sorted stage list from activeTasks
                        // When "allocated only" is ON, filter to stages with any allocation > 0 (CC always shown)
                        const standardStages = showAllocatedOnly
                          ? sortedStages.filter(stage => {
                              if (stage.key === 'cc') return true;
                              const tl = project.stageTimeline?.[stage.key] || {};
                              const b = tl.billing || {};
                              const n = (v) => Number(v ?? 0) || 0;
                              if (n(tl.invoicePercent) > 0) return true;
                              if (n(b.percent) > 0) return true;
                              if (n(b.lumpsum ?? b.lumpSum) > 0) return true;
                              if (n(project.budgetAllocation?.[stage.key]) > 0) return true;
                              // A stage with an invoice (any status) is billed even without an allocation recorded.
                              // Dates alone never count: the planning page can write a Planned or Contract date onto
                              // a scope stage that has no payment (e.g. GFC), and that must not list it here.
                              if (invoiceSummary[stage.key]) return true;
                              // Include if any part stage has allocation
                              return (tl.partStages || []).some(p => {
                                const pb = p.billing || {};
                                return n(pb.percentOfStage) > 0 || n(pb.lumpsum ?? pb.lumpSum) > 0;
                              });
                            })
                          : sortedStages;

                        // Get the current allocation values (pending edits take priority)
                        const getCurrentAllocation = (stageKey) => {
                          if (pendingAllocations.hasOwnProperty(stageKey)) {
                            return pendingAllocations[stageKey];
                          }
                          return project.budgetAllocation?.[stageKey] || 0;
                        };

                        // Calculate total allocation
                        const calculateTotalAllocation = () => {
                          return standardStages.reduce((sum, stage) => {
                            if (stage.key === 'cc') return sum; // CC is retainer-based, not part of % allocation
                            return sum + (getCurrentAllocation(stage.key) || 0);
                          }, 0);
                        };

                        const totalAllocation = calculateTotalAllocation();
                        const hasPendingChanges = Object.keys(pendingAllocations).length > 0;
                        const isValidTotal = Math.abs(totalAllocation - 100) < 0.01;

                        // Calculate reminders
                        const calculateReminders = () => {
                          const now = new Date();
                          const reminders = [];
                          
                          standardStages.forEach(stage => {
                            if (stage.key === 'cc') return; // CC is retainer-based, skip invoice reminders
                            const timeline = project.stageTimeline[stage.key];
                            if (!timeline) return;
                            
                            const stageSummary = invoiceSummary[stage.key];
                            if (timeline.invoiceDate && !timeline.paidDate && !stageSummary?.hasPaidConfirmed) {
                              const invoiceDate = new Date(timeline.invoiceDate);
                              const daysSinceInvoice = Math.floor((now - invoiceDate) / (1000 * 60 * 60 * 24));

                              if (daysSinceInvoice >= 7) {
                                reminders.push({
                                  stage: stage.name,
                                  type: 'payment',
                                  message: `Invoice unpaid for ${daysSinceInvoice} days`,
                                  severity: daysSinceInvoice >= 30 ? 'high' : 'medium'
                                });
                              }
                            }
                            
                            if (timeline.actualDate && !timeline.clientApprovalDate) {
                              const actualDate = new Date(timeline.actualDate);
                              const daysSinceCompletion = Math.floor((now - actualDate) / (1000 * 60 * 60 * 24));
                              
                              if (daysSinceCompletion >= 7) {
                                reminders.push({
                                  stage: stage.name,
                                  type: 'approval',
                                  message: `Awaiting client approval for ${daysSinceCompletion} days`,
                                  severity: daysSinceCompletion >= 14 ? 'high' : 'medium'
                                });
                              }
                            }
                          });
                          
                          return reminders;
                        };
                        
                        const reminders = calculateReminders();

                        // Build overdue lookup: Set of "stageKey:fieldKey" for overdue cells
                        const overdueFields = new Set();
                        reminders.forEach(r => {
                          const matchStage = standardStages.find(s => s.name === r.stage);
                          if (matchStage) {
                            if (r.type === 'payment') overdueFields.add(`${matchStage.key}:paidDate`);
                            if (r.type === 'approval') overdueFields.add(`${matchStage.key}:clientApprovalDate`);
                          }
                        });

                        // Determine stage status
                        const getStageStatus = (stage) => {
                          if (stage.key === 'cc') {
                            // CC: check invoice chain first (paid/invoiced), fallback to lifecycle
                            const ccTimeline = project.stageTimeline.cc || {};
                            const ccInvSummary = invoiceSummary['cc'];
                            if (ccInvSummary?.hasPaidConfirmed || ccTimeline.paidDate) {
                              return { label: 'Paid', color: 'brio-badge--approved' };
                            }
                            if (ccTimeline.invoiceDate) {
                              const ccInvoiceEmailStatus = ccTimeline.emailStatus?.invoiceDate;
                              if (ccInvoiceEmailStatus === 'sent') return { label: 'Invoiced', color: 'brio-badge--warning' };
                              return { label: 'Radar', color: 'brio-badge--info' };
                            }
                            // Fallback to lifecycle status
                            return getCCStatusBadge(getCCStatus(ccTimeline));
                          }
                          const timeline = project.stageTimeline[stage.key] || {};
                          return getStageStatusFromTimeline(timeline, invoiceSummary[stage.key]);
                        };

                        // ── Build a frozen, read-only snapshot of the timeline table exactly as
                        // it is currently displayed (respecting the "Allocated stages only" filter).
                        // Stored in timeline_versions.snapshot and rendered by ReadOnlyTimelineTable.
                        const buildCurrentSnapshot = () => {
                          const n = (v) => Number(v ?? 0) || 0;
                          const rows = [];
                          standardStages.forEach((stage) => {
                            const isCC = stage.key === 'cc';
                            const tl = project.stageTimeline[stage.key] || {};
                            const summary = invoiceSummary[stage.key];
                            const st = getStageStatus(stage);
                            const status = { label: st.label || st.status, color: st.color || '' };
                            if (isCC) {
                              rows.push({
                                key: stage.key, name: stage.name, kind: 'cc',
                                ccSummaryText: tl.startDate
                                  ? `Started ${tl.startDate}${getCCStatus(tl) === 'paused' ? ' · Paused' : ''}`
                                  : '— retainer —',
                                ccDates: {
                                  invoiceDate: summary?.lastIssuedInvoiceDate || summary?.lastInvoiceDate || tl.invoiceDate || '',
                                  paidDate: summary?.hasPaidConfirmed ? (summary.paidDate || tl.paidDate || '') : (tl.paidDate || ''),
                                },
                                status,
                              });
                            } else {
                              rows.push({
                                key: stage.key, name: stage.name, kind: 'stage',
                                dates: resolveTimelineDisplayDates(tl, summary),
                                status,
                                parts: buildSnapshotParts(stage.key, tl, invoiceSummary, showAllocatedOnly),
                              });
                            }
                          });
                          (project.miscFeeScopes || [])
                            .filter((scope) => {
                              if (!showAllocatedOnly) return true;
                              const miscKey = `misc__${scope.id}`;
                              const tl = project.stageTimeline?.[miscKey] || {};
                              const b = tl.billing || {};
                              if (n(scope.amount) > 0) return true;
                              if (n(b.lumpsum ?? b.lumpSum) > 0) return true;
                              if (n(b.percent) > 0) return true;
                              if (invoiceSummary[miscKey]) return true; // invoiced; dates alone never list a scope
                              return (tl.partStages || []).some((p) => {
                                const pb = p.billing || {};
                                return n(pb.percentOfStage) > 0 || n(pb.lumpsum ?? pb.lumpSum) > 0;
                              });
                            })
                            .forEach((scope) => {
                              const miscKey = `misc__${scope.id}`;
                              const tl = project.stageTimeline[miscKey] || {};
                              const summary = invoiceSummary[miscKey];
                              const s = getStageStatusFromTimeline(tl, summary);
                              rows.push({
                                key: miscKey, name: scope.name || miscKey, kind: 'misc',
                                dates: resolveTimelineDisplayDates(tl, summary),
                                status: { label: s.label || s.status, color: s.color || '' },
                                parts: buildSnapshotParts(miscKey, tl, invoiceSummary, showAllocatedOnly),
                              });
                            });
                          return { schema: 1, showAllocatedOnly, rows };
                        };

                        // Update function for timeline (immediate save)
                        const updateStageTimeline = async (stageKey, field, value) => {
                          const newTimeline = {
                            ...project.stageTimeline,
                            [stageKey]: {
                              ...(project.stageTimeline[stageKey] || {}),
                              [field]: value || null
                            }
                          };
                          
                          if (useSupabaseDB) {
                            const old = project.stageTimeline || {};
                            const partial = {};
                            for (const k of Object.keys(newTimeline)) { if (newTimeline[k] !== old[k]) partial[k] = newTimeline[k]; }
                            const result = await supabaseProjects.mergeProjectTimeline(project.id, partial, project.stageTimeline || {});
                            if (!result.success) alert('Error saving timeline: ' + result.error);
                          } else {
                            setProjects(projects.map(p =>
                              p.id === project.id ? { ...p, stageTimeline: newTimeline } : p
                            ));
                          }
                        };

                        // Wrapper for targetDate (planned date) changes:
                        // First entry (empty → value) is passive.
                        // Modification (value → different value) triggers timeline_change.
                        const handleTargetDateChange = (stageKey, newValue, stageName) => {
                          // A SYNC-LOCKED main stage is frozen (read-only, like paid) — block its own Planned edit.
                          // Unlocked stages (with or without parts) are editable and sync from planning.
                          if (project.stageTimeline?.[stageKey]?._syncLocked) return;
                          const previousValue = project.stageTimeline?.[stageKey]?.targetDate || null;
                          updateStageTimeline(stageKey, 'targetDate', newValue);

                          // Pass planned date threshold for trigger gating
                          const plannedDateThreshold = (currentClientMemo?.plannedDateTriggerThresholdDays ?? project?.plannedDateTriggerThresholdDays ?? 7);
                          const triggerType = resolveTimelineTrigger({
                            fieldKey: 'targetDate',
                            previousValue,
                            nextValue: newValue || null,
                            stageKey,
                            plannedDateThreshold,
                          });

                          if (triggerType) {
                            const meta = {
                              stageKey,
                              stageName: stageName || stageKey,
                              projectName: project?.name || project?.projectName || '',
                              projectCode: project?.projectCode || '',
                              previousDate: previousValue,
                              revisedDate: newValue,
                              routing: 'native_mail',
                            };
                            recActions.generateFromTrigger(triggerType, meta).then(created => {
                              if (created.length > 0) {
                                setNextActionsContext({
                                  triggerType,
                                  stageName: stageName || stageKey,
                                  stageKey,
                                  actions: created,
                                  projectId: project?.id,
                                  routing: 'native_mail',
                                });
                              }
                            });
                          }
                        };

                        // Update CC lifecycle (replaces entire cc object) — optimistic local update
                        const updateCCTimeline = async (ccData) => {
                          const newTimeline = {
                            ...project.stageTimeline,
                            cc: ccData
                          };
                          
                          // Optimistic: update local state immediately so UI stays responsive
                          setProjects(prev => prev.map(p => 
                            p.id === project.id ? { ...p, stageTimeline: newTimeline } : p
                          ));

                          if (useSupabaseDB) {
                            const old = project.stageTimeline || {};
                            const partial = {};
                            for (const k of Object.keys(newTimeline)) { if (newTimeline[k] !== old[k]) partial[k] = newTimeline[k]; }
                            const result = await supabaseProjects.mergeProjectTimeline(project.id, partial, project.stageTimeline || {});
                            if (!result.success) alert('Error saving CC timeline: ' + (result.error || 'Unknown error'));
                          }
                        };

                        // ── Reset CC: clears start date + lifecycle fields, keeps invoices in DB untouched ──
                        const handleResetCC = async () => {
                          // Clear CC lifecycle fields from stageTimeline.cc
                          // Preserve any billing/planning data but clear all lifecycle state
                          const clearedCC = {
                            startDate: null,
                            stopDate: null,
                            pausedPeriods: [],
                            invoices: [],
                            ...(project.stageTimeline?.cc?.billing ? { billing: project.stageTimeline.cc.billing } : {}),
                          };
                          const newTimeline = {
                            ...project.stageTimeline,
                            cc: clearedCC,
                          };

                          // Optimistic local update
                          setProjects(prev => prev.map(p =>
                            p.id === project.id ? { ...p, stageTimeline: newTimeline } : p
                          ));

                          if (useSupabaseDB) {
                            const old = project.stageTimeline || {};
                            const partial = {};
                            for (const k of Object.keys(newTimeline)) { if (newTimeline[k] !== old[k]) partial[k] = newTimeline[k]; }
                            const result = await supabaseProjects.mergeProjectTimeline(project.id, partial, project.stageTimeline || {});
                            if (!result.success) alert('Error resetting CC: ' + (result.error || 'Unknown error'));
                          }
                        };

                        // ── Part Stage CRUD ──
                        const addPartStage = async (parentKey) => {
                          const parentTimeline = project.stageTimeline[parentKey] || {};
                          const existing = parentTimeline.partStages || [];
                          // Look up parent name: first standard stages, then misc scopes
                          let parentName = parentKey;
                          const parentStage = standardStages.find(s => s.key === parentKey);
                          if (parentStage) {
                            parentName = parentStage.name;
                          } else if (parentKey.startsWith('misc__')) {
                            const scopeId = parentKey.replace('misc__', '');
                            const scope = (project.miscFeeScopes || []).find(s => s.id === scopeId);
                            if (scope?.name) parentName = scope.name;
                          }
                          const newPart = {
                            id: generatePartStageId(),
                            name: `${parentName} — Part ${existing.length + 1}`,
                            contractDate: null, targetDate: null, actualDate: null,
                            clientApprovalDate: null, invoiceDate: null, paidDate: null,
                          };
                          const newTimeline = {
                            ...project.stageTimeline,
                            [parentKey]: { ...parentTimeline, partStages: [newPart, ...existing] },
                          };
                          // Optimistic local update
                          setProjects(prev => prev.map(p =>
                            p.id === project.id ? { ...p, stageTimeline: newTimeline } : p
                          ));
                          if (useSupabaseDB) {
                            const result = await supabaseProjects.mergeProjectTimeline(project.id, { [parentKey]: newTimeline[parentKey] }, project.stageTimeline || {});
                            if (!result.success) alert('Error adding part stage: ' + (result.error || 'Unknown error'));
                          }
                        };

                        const deletePartStage = async (parentKey, partId) => {
                          const partKey = derivePartStageKey(parentKey, partId);
                          const partSummary = invoiceSummary[partKey];
                          if (partSummary?.hasIssuedInvoice || partSummary?.hasPaidConfirmed) {
                            alert('Cannot delete a part stage that has issued or paid invoices. Delete the invoices first.');
                            return;
                          }
                          if (!window.confirm('Delete this part stage?')) return;
                          const parentTimeline = project.stageTimeline[parentKey] || {};
                          const updated = (parentTimeline.partStages || []).filter(p => p.id !== partId);
                          // Build updated timeline removing the part stage
                          let newTimeline = {
                            ...project.stageTimeline,
                            [parentKey]: { ...parentTimeline, partStages: updated },
                          };
                          // Unassign any tasks in the timeline rows that were assigned to this sub-stage
                          const rows = project.stageTimeline._rows || [];
                          rows.forEach(r => {
                            if (r.indent > 0 && newTimeline[r.id]?._subStageId === partId) {
                              newTimeline = {
                                ...newTimeline,
                                [r.id]: { ...newTimeline[r.id], _subStageId: null },
                              };
                            }
                          });
                          setProjects(prev => prev.map(p =>
                            p.id === project.id ? { ...p, stageTimeline: newTimeline } : p
                          ));
                          if (useSupabaseDB) {
                            // partStages change lives in parentKey; _subStageId cleanup may touch row keys too
                            const old = project.stageTimeline || {};
                            const partial = {};
                            for (const k of Object.keys(newTimeline)) { if (newTimeline[k] !== old[k]) partial[k] = newTimeline[k]; }
                            const result = await supabaseProjects.mergeProjectTimeline(project.id, partial, project.stageTimeline || {});
                            if (!result.success) alert('Error deleting part stage: ' + (result.error || 'Unknown error'));
                          }
                        };

                        const updatePartStageField = async (parentKey, partId, field, value) => {
                          const parentTimeline = project.stageTimeline[parentKey] || {};
                          const parts = (parentTimeline.partStages || []).map(p =>
                            p.id === partId ? { ...p, [field]: value || null } : p
                          );
                          const newTimeline = {
                            ...project.stageTimeline,
                            [parentKey]: { ...parentTimeline, partStages: parts },
                          };
                          setProjects(prev => prev.map(p =>
                            p.id === project.id ? { ...p, stageTimeline: newTimeline } : p
                          ));
                          if (useSupabaseDB) {
                            const result = await supabaseProjects.mergeProjectTimeline(project.id, { [parentKey]: newTimeline[parentKey] }, project.stageTimeline || {});
                            if (!result.success) alert('Error saving part stage: ' + (result.error || 'Unknown error'));
                          }
                        };

                        // Update pending allocation (local state only)
                        const updatePendingAllocation = (stageKey, value) => {
                          setPendingAllocations(prev => ({
                            ...prev,
                            [stageKey]: parseFloat(value) || 0
                          }));
                        };

                        // Confirm and save budget allocation
                        const confirmBudgetAllocation = async () => {
                          if (!isValidTotal) return;
                          
                          setIsSaving(true);
                          
                          const newBudgetAllocation = {
                            ...(project.budgetAllocation || {})
                          };
                          
                          Object.keys(pendingAllocations).forEach(key => {
                            newBudgetAllocation[key] = pendingAllocations[key];
                          });
                          
                          if (useSupabaseDB) {
                            const result = await supabaseProjects.updateProject(project.id, { budgetAllocation: newBudgetAllocation });
                            if (!result.success) {
                              alert('Error saving budget allocation: ' + result.error);
                              setIsSaving(false);
                              return;
                            }
                          } else {
                            setProjects(projects.map(p => 
                              p.id === project.id ? { ...p, budgetAllocation: newBudgetAllocation } : p
                            ));
                          }
                          
                          setPendingAllocations({});
                          setIsSaving(false);
                        };

                        // Discard pending changes
                        const discardChanges = () => {
                          setPendingAllocations({});
                        };

                        return (
                          <>
                            {/* Project Header */}
                            <div className="brio-mb-s" style={{ padding: '16px', background: 'var(--brio-gold-light)', border: '1px solid var(--brio-gold)' }}>
                              <div className="brio-flex brio-justify-between brio-items-center">
                                <div>
                                  <h3 className="brio-font-bold">{project.name}</h3>
                                  <div className="brio-text-xs brio-text-grey brio-mt-xs">
                                    Client: {client?.name} | Budget: {fmtCurr(project.budget)}
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* Active Reminders */}
                            {reminders.length > 0 && (
                              <div className="brio-mb-s" style={{ padding: '16px', background: 'var(--brio-red-light)', border: '1px solid var(--brio-red)' }}>
                                <h4 className="brio-font-bold brio-mb-xs" style={{ color: 'var(--brio-red)' }}>
                                  🔔 Active Reminders ({reminders.length})
                                </h4>
                                <div className="brio-flex brio-flex-col brio-gap-xs">
                                  {reminders.map((r, idx) => (
                                    <div key={idx} className={`brio-text-s ${r.severity === 'high' ? 'brio-font-bold' : ''}`} style={{ color: 'var(--brio-red)' }}>
                                      • {r.stage}: {r.message}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Timeline Table */}
                            <div className="brio-flex brio-justify-between brio-items-center brio-mb-xs" style={{ flexWrap: 'wrap', gap: '6px' }}>
                              <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '9px', color: 'var(--brio-grey)', cursor: viewingVersion ? 'default' : 'pointer' }}>
                                <input
                                  type="checkbox"
                                  checked={viewingVersion ? !!viewingVersion.snapshot?.showAllocatedOnly : showAllocatedOnly}
                                  disabled={!!viewingVersion}
                                  onChange={e => setShowAllocatedOnly(e.target.checked)}
                                  style={{ accentColor: 'var(--brio-gold)' }}
                                />
                                Allocated stages only
                              </label>
                              <VersionsDropdown
                                versions={timelineVersionsForUi}
                                viewingVersionId={viewingVersionId}
                                saving={savingVersion}
                                canManage={canEditProject}
                                onAdd={async () => { const snap = buildCurrentSnapshot(); await saveTimelineVersion(snap); }}
                                onSelect={(v) => setViewingVersionId(v.id)}
                                onReturnToCurrent={() => setViewingVersionId(null)}
                                onDelete={async (v) => {
                                  if (window.confirm(`Delete ${v.label}? This snapshot will be permanently removed.`)) {
                                    if (viewingVersionId === v.id) setViewingVersionId(null);
                                    await timelineVersionsHook.deleteVersion(v.id, currentProjectIdMemo);
                                  }
                                }}
                              />
                            </div>
                            {viewingVersion ? (
                              <ReadOnlyTimelineTable
                                snapshot={viewingVersion.snapshot}
                                tableProps={tableProps}
                                versionLabel={viewingVersion.label}
                                createdAt={viewingVersion.createdAt}
                                onExit={() => setViewingVersionId(null)}
                              />
                            ) : (
                            <BrioByCode code={PRESENTATION_CODE} className="!overflow-visible">
                              <BrioTable {...tableProps}>
                                <thead>
                                  <tr>
                                    <th style={{ width: 24 }}></th>
                                    <th style={{ minWidth: 70 }}>Stage</th>
                                    <th style={{ minWidth: 58 }}><ColumnHeaderWithInfo label="Contract" tooltip={COLUMN_TOOLTIPS.Contract} note="(Based on contract terms)" /></th>
                                    <th style={{ minWidth: 58 }}><ColumnHeaderWithInfo label="Planned" tooltip={COLUMN_TOOLTIPS.Planned} note="(Current planning/ target date)" /></th>
                                    <th style={{ minWidth: 58 }}><ColumnHeaderWithInfo label="Actual" tooltip={COLUMN_TOOLTIPS.Actual} note="(Actual stage completion)" /></th>
                                    <th style={{ minWidth: 58 }}><ColumnHeaderWithInfo label="Proforma" tooltip={COLUMN_TOOLTIPS.Proforma} /></th>
                                    <th style={{ minWidth: 58 }}><ColumnHeaderWithInfo label="Appr." tooltip={COLUMN_TOOLTIPS['Appr.']} /></th>
                                    <th style={{ minWidth: 58 }}><ColumnHeaderWithInfo label="Invoice" tooltip={COLUMN_TOOLTIPS.Invoice} /></th>
                                    <th style={{ minWidth: 58 }}><ColumnHeaderWithInfo label="Paid" tooltip={COLUMN_TOOLTIPS.Paid} /></th>
                                    <th style={{ minWidth: 70 }}>Status</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {standardStages.map((stage) => {
                                    const isCC = stage.key === 'cc';
                                    const timeline = project.stageTimeline[stage.key] || {};
                                    const allocation = getCurrentAllocation(stage.key);
                                    const statusInfo = getStageStatus(stage);
                                    // Paid stages lock their dates against manual edits + Timelines-page sync.
                                    // The pencil icon toggles a temporary manual-edit unlock (unlockedStages).
                                    const stageIsPaid = !isCC && (statusInfo.label || statusInfo.status) === 'Paid';
                                    // Sync-lock (persisted `_syncLocked`): the pen/lock freezes ANY main stage from the
                                    // planning-page sync, like a paid stage. A sync-locked stage is read-only here until it
                                    // is unlocked (which re-enables sync). Paid takes precedence: a paid stage always uses its
                                    // temporary local unlock (unlockedStages) via the pen, so a stale _syncLocked can never
                                    // trap a paid stage read-only — it re-applies only if the stage is no longer paid.
                                    const isSyncLocked = !isCC && !!timeline._syncLocked;
                                    const stageLocked = canEditProject && (stageIsPaid ? !unlockedStages[stage.key] : isSyncLocked);
                                    const hasLocalChange = pendingAllocations.hasOwnProperty(stage.key);
                                    const isExpanded = expandedStageKey === stage.key;
                                    // Only CC rows can expand (lifecycle panel)
                                    const canExpand = isCC && canEditProject;
                                    const showExpanded = canExpand && isExpanded;
                                    
                                    return (
                                      <React.Fragment key={stage.key}>
                                        <tr className="brio-row--hoverable"
                                          style={showExpanded ? { background: 'var(--brio-gold-light)' } : undefined}>
                                          {/* Caret toggle (CC only) */}
                                          <td style={{ padding: '2px 4px', cursor: canExpand ? 'pointer' : 'default', textAlign: 'center' }}
                                            onClick={() => {
                                              if (!canExpand) return;
                                              setExpandedStageKey(isExpanded ? null : stage.key);
                                            }}>
                                            {canExpand ? (
                                              showExpanded
                                                ? <ChevronDown className="w-3 h-3" style={{ color: 'var(--brio-grey)', display: 'inline' }} />
                                                : <ChevronRight className="w-3 h-3" style={{ color: 'var(--brio-grey)', display: 'inline' }} />
                                            ) : null}
                                          </td>
                                          <td className="brio-font-medium">
                                            <div className="brio-flex brio-items-center brio-justify-between" style={{ gap: '2px' }}>
                                              <span title={stage.name}>{getStageAbbreviation(stage.name)}</span>
                                              {!isCC && canEditProject && (
                                                <button
                                                  onClick={(e) => { e.stopPropagation(); addPartStage(stage.key); }}
                                                  title={`Add part stage under ${stage.name}`}
                                                  style={{
                                                    background: 'none', border: 'none', cursor: 'pointer',
                                                    padding: '0 2px', fontSize: '9px', color: 'var(--brio-grey)',
                                                    lineHeight: 1, flexShrink: 0,
                                                  }}
                                                >
                                                  <Plus className="w-3 h-3" />
                                                </button>
                                              )}
                                            </div>
                                          </td>
                                          {/* Date columns: CC gets summary for first 5 + separate Invoice/Paid; others get 7 date inputs */}
                                          {isCC ? (
                                            <>
                                              <td colSpan={5} className="brio-text-xs brio-text-grey" style={{ textAlign: 'center' }}>
                                                {timeline.startDate
                                                  ? `Started ${timeline.startDate}${getCCStatus(timeline) === 'paused' ? ' · Paused' : ''}`
                                                  : '— retainer —'}
                                              </td>
                                              {/* CC Invoice date — shows invoice-derived date if issued */}
                                              <td className={getTimelineCellColor({ columnKey: 'invoiceDate', value: timeline.invoiceDate || invoiceSummary['cc']?.lastIssuedInvoiceDate })}>
                                                {(() => {
                                                  const ccSummary = invoiceSummary['cc'];
                                                  const ccInvDate = ccSummary?.lastIssuedInvoiceDate || ccSummary?.lastInvoiceDate;
                                                  const effectiveDate = ccInvDate || timeline.invoiceDate || '';
                                                  if (ccSummary?.hasIssuedInvoice && ccInvDate && !canEditProject) {
                                                    const pp = ccInvDate.split('-');
                                                    const fmt = pp.length === 3 ? `${pp[0]}-${pp[1]}-${pp[2]}` : ccInvDate;
                                                    return (
                                                      <div style={{ display: 'flex', alignItems: 'center' }}>
                                                        <div title="Invoice date from Invoices page" style={{
                                                          width: '100%', height: '22px', boxSizing: 'border-box', border: '1px solid var(--brio-border)', padding: '0 4px',
                                                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: '9px', fontFamily: 'var(--brio-font-family)',
                                                          background: 'var(--brio-gold-light, #fef9e7)', color: 'var(--brio-gold, #9F902A)',
                                                        }}>{fmt}</div>
                                                        <EmailStatusMarker
                                                          status={deriveEmailStatus('cc', 'invoiceDate', ccInvDate || effectiveDate)}
                                                          onChange={(s) => updateEmailStatus('cc', 'invoiceDate', s)} />
                                                      </div>
                                                    );
                                                  }
                                                  const ccInputValue = (canEditProject && ccInvDate) ? (timeline.invoiceDate || '') : effectiveDate;
                                                  return (
                                                    <div style={{ display: 'flex', alignItems: 'center' }}>
                                                      <CompactDateInput
                                                        value={ccInputValue}
                                                        onChange={(e) => {
                                                          handleDateChangeWithEmail('cc', 'invoiceDate', e.target.value, stage.name, { oldInvoiceValue: timeline.invoiceDate });
                                                        }} />
                                                      <EmailStatusMarker
                                                        status={deriveEmailStatus('cc', 'invoiceDate', ccInputValue)}
                                                        onChange={(s) => updateEmailStatus('cc', 'invoiceDate', s)} />
                                                    </div>
                                                  );
                                                })()}
                                              </td>
                                              {/* CC Paid — locked when invoice paid_confirmed */}
                                              <td className={getTimelineCellColor({ columnKey: 'paidDate', value: timeline.paidDate || invoiceSummary['cc']?.hasPaidConfirmed })}>
                                                {(() => {
                                                  const ccStageSummary = invoiceSummary['cc'];
                                                  if (ccStageSummary?.hasPaidConfirmed && !canEditProject) {
                                                    const displayDate = ccStageSummary.paidDate || timeline.paidDate || '';
                                                    const parts = displayDate ? displayDate.split('-') : [];
                                                    const formatted = parts.length === 3
                                                      ? `${parts[0]}-${parts[1]}-${parts[2]}`
                                                      : '—';
                                                    return (
                                                      <div
                                                        title="Payment confirmed via Invoices page — locked"
                                                        style={{
                                                          width: '100%', height: '22px', boxSizing: 'border-box',
                                                          border: '1px solid var(--brio-border)', padding: '0 4px',
                                                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: '9px', fontFamily: 'var(--brio-font-family)',
                                                          background: 'var(--brio-green-light, #f0fdf4)',
                                                          color: 'var(--brio-green, #16a34a)',
                                                          cursor: 'not-allowed',
                                                        }}
                                                      >
                                                        ✓ {formatted}
                                                      </div>
                                                    );
                                                  }
                                                  return (
                                                    <div style={{ display: 'flex', alignItems: 'center' }}>
                                                      <CompactDateInput
                                                        value={ccStageSummary?.hasPaidConfirmed ? (ccStageSummary.paidDate || timeline.paidDate || '') : (timeline.paidDate || '')}
                                                        onChange={(e) => handleDateChangeWithEmail('cc', 'paidDate', e.target.value, stage.name)} />
                                                      <EmailStatusMarker
                                                        status={deriveEmailStatus('cc', 'paidDate', timeline.paidDate)}
                                                        onChange={(s) => updateEmailStatus('cc', 'paidDate', s)} />
                                                    </div>
                                                  );
                                                })()}
                                              </td>
                                            </>
                                          ) : (
                                            <>
                                              <td className={getTimelineCellColor({ columnKey: 'contractDate', value: timeline.contractDate })}>
                                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                                  <CompactDateInput
                                                    locked={stageLocked}
                                                    value={timeline.contractDate || ''}
                                                    onChange={(e) => {
                                                      handleDateChangeWithEmail(stage.key, 'contractDate', e.target.value, stage.name);
                                                    }} />
                                                  <EmailStatusMarker status="not_applicable" disabled />
                                                </div>
                                              </td>
                                              <td className={getTimelineCellColor({ columnKey: 'targetDate', value: timeline.targetDate })}>
                                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                                  <CompactDateInput
                                                    locked={stageLocked}
                                                    value={timeline.targetDate || ''}
                                                    onChange={(e) => handleTargetDateChange(stage.key, e.target.value, stage.name)} />
                                                  {stageLocked
                                                    ? <LockedDot title={stageIsPaid ? "Planned date locked (paid stage) — frozen from planning sync" : "Planned date locked — frozen from planning sync"} />
                                                    : <EmailStatusMarker status="not_applicable" disabled />}
                                                </div>
                                              </td>
                                              <td className={getTimelineCellColor({ columnKey: 'actualDate', value: timeline.actualDate })}>
                                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                                  <CompactDateInput
                                                    locked={stageLocked}
                                                    value={timeline.actualDate || ''}
                                                    onChange={(e) => {
                                                      handleDateChangeWithEmail(stage.key, 'actualDate', e.target.value, stage.name);
                                                    }} />
                                                  <ActualStatusMarker
                                                    actualDate={timeline.actualDate}
                                                    plannedDate={timeline.targetDate}
                                                    status={deriveEmailStatus(stage.key, 'actualDate', timeline.actualDate)}
                                                    onChange={(s) => updateEmailStatus(stage.key, 'actualDate', s)} />
                                                </div>
                                                {recentEdits[`${stage.key}:actualDate`] && <ContextHint text={CONTEXT_HINTS.actualDate} />}
                                              </td>
                                              {/* Proforma Invoice Date — optional, only for projects that require proforma invoicing */}
                                              <td className={getTimelineCellColor({ columnKey: 'proformaInvoiceDate', value: timeline.proformaInvoiceDate || invoiceSummary[stage.key]?.lastProformaInvoiceDate })}>
                                                {renderProformaCell(invoiceSummary[stage.key], timeline, stage.key, stage.name, { locked: stageLocked })}
                                                {recentEdits[`${stage.key}:proformaInvoiceDate`] && <ContextHint text={CONTEXT_HINTS.proformaInvoiceDate} />}
                                              </td>
                                              <td className={getTimelineCellColor({ columnKey: 'clientApprovalDate', value: timeline.clientApprovalDate, isOverdue: overdueFields.has(`${stage.key}:clientApprovalDate`) })}>
                                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                                  <CompactDateInput
                                                    locked={stageLocked}
                                                    value={timeline.clientApprovalDate || ''}
                                                    onChange={(e) => {
                                                      handleDateChangeWithEmail(stage.key, 'clientApprovalDate', e.target.value, stage.name);
                                                    }} />
                                                  <EmailStatusMarker status="not_applicable" disabled />
                                                </div>
                                              </td>
                                              <td className={getTimelineCellColor({ columnKey: 'invoiceDate', value: timeline.invoiceDate || invoiceSummary[stage.key]?.lastIssuedInvoiceDate })}>
                                                {(() => {
                                                  // Show invoice-derived date if issued invoice exists
                                                  const stageSummary = invoiceSummary[stage.key];
                                                  const invDate = stageSummary?.lastIssuedInvoiceDate || stageSummary?.lastInvoiceDate;
                                                  // A timeline invoiceDate on a proforma-only stage belongs to the
                                                  // Proforma column — don't echo it here as a tax invoice date.
                                                  const timelineInvDate = (stageSummary?.hasProformaInvoice && !stageSummary?.hasIssuedInvoice) ? '' : (timeline.invoiceDate || '');
                                                  const effectiveDate = invDate || timelineInvDate;
                                                  if (stageLocked) return <LockedDateBox value={effectiveDate} />;
                                                  if (stageSummary?.hasIssuedInvoice && invDate && !canEditProject) {
                                                    // Show issued invoice date as read-only source of truth for non-admins/supervisors
                                                    const fmtParts = invDate.split('-');
                                                    const formatted = fmtParts.length === 3 ? `${fmtParts[0]}-${fmtParts[1]}-${fmtParts[2]}` : invDate;
                                                    return (
                                                      <div style={{ display: 'flex', alignItems: 'center' }}>
                                                        <div
                                                          title="Invoice date from Invoices page"
                                                          style={{
                                                            width: '100%', height: '22px', boxSizing: 'border-box',
                                                            border: '1px solid var(--brio-border)', padding: '0 4px',
                                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: '9px', fontFamily: 'var(--brio-font-family)',
                                                            background: 'var(--brio-gold-light, #fef9e7)',
                                                            color: 'var(--brio-gold, #9F902A)',
                                                          }}
                                                        >{formatted}</div>
                                                        <EmailStatusMarker
                                                          status={deriveEmailStatus(stage.key, 'invoiceDate', invDate)}
                                                          onChange={(s) => updateEmailStatus(stage.key, 'invoiceDate', s)} />
                                                      </div>
                                                    );
                                                  }
                                                  const stageInputValue = (canEditProject && invDate) ? (timeline.invoiceDate || '') : effectiveDate;
                                                  return (
                                                    <>
                                                      <div style={{ display: 'flex', alignItems: 'center' }}>
                                                        <CompactDateInput
                                                          value={stageInputValue}
                                                          onChange={(e) => {
                                                            handleDateChangeWithEmail(stage.key, 'invoiceDate', e.target.value, stage.name, { oldInvoiceValue: timeline.invoiceDate });
                                                          }} />
                                                        <EmailStatusMarker
                                                          status={deriveEmailStatus(stage.key, 'invoiceDate', stageInputValue)}
                                                          onChange={(s) => updateEmailStatus(stage.key, 'invoiceDate', s)} />
                                                      </div>
                                                      {recentEdits[`${stage.key}:invoiceDate`] && <ContextHint text={CONTEXT_HINTS.invoiceDate} />}
                                                    </>
                                                  );
                                                })()}
                                              </td>
                                              <td className={getTimelineCellColor({ columnKey: 'paidDate', value: timeline.paidDate || invoiceSummary[stage.key]?.hasPaidConfirmed, isOverdue: overdueFields.has(`${stage.key}:paidDate`) })}>
                                                {(() => {
                                                  // B3: Lock paidDate when invoice has paid_confirmed (admin/supervisor can still override)
                                                  const stageSummary = invoiceSummary[stage.key];
                                                  if (stageLocked) return <LockedDateBox value={stageSummary?.hasPaidConfirmed ? (stageSummary.paidDate || timeline.paidDate || '') : (timeline.paidDate || '')} />;
                                                  if (stageSummary?.hasPaidConfirmed && !canEditProject) {
                                                    // Show the invoice's paid_date, locked (read-only for non-admin/supervisor)
                                                    const displayDate = stageSummary.paidDate || timeline.paidDate || '';
                                                    const datePts = displayDate ? displayDate.split('-') : [];
                                                    const formatted = datePts.length === 3
                                                      ? `${datePts[0]}-${datePts[1]}-${datePts[2]}`
                                                      : '—';
                                                    return (
                                                      <div
                                                        title="Payment confirmed via Invoices page — locked"
                                                        style={{
                                                          width: '100%', height: '22px', boxSizing: 'border-box',
                                                          border: '1px solid var(--brio-border)', padding: '0 4px',
                                                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: '9px', fontFamily: 'var(--brio-font-family)',
                                                          background: 'var(--brio-green-light, #f0fdf4)',
                                                          color: 'var(--brio-green, #16a34a)',
                                                          cursor: 'not-allowed',
                                                        }}
                                                      >
                                                        ✓ {formatted}
                                                      </div>
                                                    );
                                                  }
                                                  // Normal editable date input (admin/supervisor can edit even when confirmed)
                                                  return (
                                                    <>
                                                      <div style={{ display: 'flex', alignItems: 'center' }}>
                                                        <CompactDateInput
                                                          value={stageSummary?.hasPaidConfirmed ? (stageSummary.paidDate || timeline.paidDate || '') : (timeline.paidDate || '')}
                                                          onChange={(e) => handleDateChangeWithEmail(stage.key, 'paidDate', e.target.value, stage.name)} />
                                                        <EmailStatusMarker
                                                          status={deriveEmailStatus(stage.key, 'paidDate', timeline.paidDate)}
                                                          onChange={(s) => updateEmailStatus(stage.key, 'paidDate', s)} />
                                                      </div>
                                                      {recentEdits[`${stage.key}:paidDate`] && <ContextHint text={CONTEXT_HINTS.paidDate} />}
                                                    </>
                                                  );
                                                })()}
                                              </td>
                                            </>
                                          )}
                                          <td>
                                            <div className="brio-flex brio-items-center" style={{ gap: '4px' }}>
                                              <span className={`brio-badge ${statusInfo.color}`}>
                                                {statusInfo.label || statusInfo.status}
                                              </span>
                                              {canEditProject && !isCC && (
                                                <LockToggleButton
                                                  locked={stageIsPaid ? stageLocked : isSyncLocked}
                                                  onToggle={() => stageIsPaid ? toggleStageUnlock(stage.key) : toggleStageSyncLock(stage.key)}
                                                  name={stage.name} />
                                              )}
                                            </div>
                                          </td>
                                        </tr>
                                        {/* ── Part stage rows ── */}
                                        {!isCC && (timeline.partStages || [])
                                          .filter(part => {
                                            if (!showAllocatedOnly) return true;
                                            const pb = part.billing || {};
                                            const n = (v) => Number(v ?? 0) || 0;
                                            return n(pb.percentOfStage) > 0 || n(pb.lumpsum ?? pb.lumpSum) > 0;
                                          })
                                          .sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { numeric: true, sensitivity: 'base' }))
                                          .map((part) => {
                                          const partKey = derivePartStageKey(stage.key, part.id);
                                          const partSummary = invoiceSummary[partKey];
                                          const partStatus = getStageStatusFromTimeline(part, partSummary);
                                          const partIsPaid = (partStatus.label || partStatus.status) === 'Paid';
                                          const partLocked = partIsPaid && canEditProject && !unlockedStages[partKey];
                                          return (
                                            <tr key={partKey} style={{ background: 'var(--brio-grey-light)', fontSize: '10px' }}>
                                              {/* Caret placeholder */}
                                              <td />
                                              {/* Indented name + delete */}
                                              <td style={{ paddingLeft: '20px' }}>
                                                <div className="brio-flex brio-items-center" style={{ gap: '2px' }}>
                                                  <span className="brio-text-grey" style={{ fontSize: '9px' }}>↳</span>
                                                  {(editingPartName?.stageKey === stage.key && editingPartName?.partId === part.id) ? (
                                                    <input
                                                      autoFocus
                                                      type="text"
                                                      value={partNameValue}
                                                      placeholder="e.g. Main house, Architecture"
                                                      onChange={(e) => setPartNameValue(e.target.value)}
                                                      onClick={(e) => e.stopPropagation()}
                                                      onBlur={() => {
                                                        const v = partNameValue.trim();
                                                        if (v && v !== part.name) updatePartStageField(stage.key, part.id, 'name', v);
                                                        setEditingPartName(null);
                                                      }}
                                                      onKeyDown={(e) => {
                                                        if (e.key === 'Enter') {
                                                          const v = partNameValue.trim();
                                                          if (v && v !== part.name) updatePartStageField(stage.key, part.id, 'name', v);
                                                          setEditingPartName(null);
                                                        }
                                                        if (e.key === 'Escape') setEditingPartName(null);
                                                      }}
                                                      style={{ flex: 1, minWidth: 0, fontSize: '10px', fontWeight: 500, fontFamily: 'Raleway, sans-serif', border: '1px solid var(--brio-gold)', padding: '1px 4px', outline: 'none', background: '#fffef5', color: '#333' }}
                                                    />
                                                  ) : (
                                                    <span
                                                      className="brio-font-medium"
                                                      style={{ fontSize: '10px', cursor: canEditProject ? 'text' : 'default' }}
                                                      title={canEditProject ? `${part.name} — click to rename` : part.name}
                                                      onClick={canEditProject ? (e) => {
                                                        e.stopPropagation();
                                                        setEditingPartName({ stageKey: stage.key, partId: part.id });
                                                        setPartNameValue(part.name || '');
                                                      } : undefined}
                                                    >
                                                      {getPartStageAbbreviation(part.name)}
                                                    </span>
                                                  )}
                                                  {canEditProject && (
                                                    <button
                                                      onClick={(e) => { e.stopPropagation(); deletePartStage(stage.key, part.id); }}
                                                      title="Delete part stage"
                                                      style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0 1px', flexShrink: 0 }}
                                                    >
                                                      <Trash2 className="w-3 h-3" style={{ color: 'var(--brio-red)' }} />
                                                    </button>
                                                  )}
                                                </div>
                                              </td>
                                              {/* 7 date columns: Contract / Planned / Actual / Proforma / Appr. / Invoice / Paid */}
                                              <td className={getTimelineCellColor({ columnKey: 'contractDate', value: part.contractDate })}>
                                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                                  <CompactDateInput
                                                    locked={partLocked}
                                                    value={part.contractDate || ''}
                                                    onChange={(e) => {
                                                      handleDateChangeWithEmail(stage.key, 'contractDate', e.target.value, part.name, { partId: part.id });
                                                    }} />
                                                  <EmailStatusMarker status="not_applicable" disabled />
                                                </div>
                                              </td>
                                              <td className={getTimelineCellColor({ columnKey: 'targetDate', value: part.targetDate })}>
                                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                                  <CompactDateInput
                                                    locked={partLocked}
                                                    value={part.targetDate || ''}
                                                    onChange={(e) => updatePartStageField(stage.key, part.id, 'targetDate', e.target.value)} />
                                                  {partLocked
                                                    ? <LockedDot title="Planned date locked (paid) — frozen from planning sync" />
                                                    : <EmailStatusMarker status="not_applicable" disabled />}
                                                </div>
                                              </td>
                                              <td className={getTimelineCellColor({ columnKey: 'actualDate', value: part.actualDate })}>
                                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                                  <CompactDateInput
                                                    locked={partLocked}
                                                    value={part.actualDate || ''}
                                                    onChange={(e) => {
                                                      handleDateChangeWithEmail(stage.key, 'actualDate', e.target.value, part.name, { partId: part.id });
                                                    }} />
                                                  <ActualStatusMarker
                                                    actualDate={part.actualDate}
                                                    plannedDate={part.targetDate}
                                                    status={deriveEmailStatus(stage.key, 'actualDate', part.actualDate, { partId: part.id })}
                                                    onChange={(s) => updateEmailStatus(stage.key, 'actualDate', s, { partId: part.id })} />
                                                </div>
                                              </td>
                                              <td className={getTimelineCellColor({ columnKey: 'proformaInvoiceDate', value: part.proformaInvoiceDate || partSummary?.lastProformaInvoiceDate })}>
                                                {renderProformaCell(partSummary, part, stage.key, part.name, { partId: part.id, locked: partLocked })}
                                              </td>
                                              <td className={getTimelineCellColor({ columnKey: 'clientApprovalDate', value: part.clientApprovalDate })}>
                                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                                  <CompactDateInput
                                                    locked={partLocked}
                                                    value={part.clientApprovalDate || ''}
                                                    onChange={(e) => {
                                                      handleDateChangeWithEmail(stage.key, 'clientApprovalDate', e.target.value, part.name, { partId: part.id });
                                                    }} />
                                                  <EmailStatusMarker status="not_applicable" disabled />
                                                </div>
                                              </td>
                                              <td className={getTimelineCellColor({ columnKey: 'invoiceDate', value: part.invoiceDate || partSummary?.lastIssuedInvoiceDate })}>
                                                {(() => {
                                                  const psInvDate = partSummary?.lastIssuedInvoiceDate || partSummary?.lastInvoiceDate;
                                                  // A timeline invoiceDate on a proforma-only part belongs to the
                                                  // Proforma column — don't echo it here as a tax invoice date.
                                                  const psTimelineInvDate = (partSummary?.hasProformaInvoice && !partSummary?.hasIssuedInvoice) ? '' : (part.invoiceDate || '');
                                                  const effectiveDate = psInvDate || psTimelineInvDate;
                                                  if (partLocked) return <LockedDateBox value={effectiveDate} />;
                                                  if (partSummary?.hasIssuedInvoice && psInvDate && !canEditProject) {
                                                    const pp = psInvDate.split('-');
                                                    const fmt = pp.length === 3 ? `${pp[0]}-${pp[1]}-${pp[2]}` : psInvDate;
                                                    return (
                                                      <div style={{ display: 'flex', alignItems: 'center' }}>
                                                        <div title="Invoice date from Invoices page" style={{
                                                          width: '100%', height: '22px', boxSizing: 'border-box', border: '1px solid var(--brio-border)', padding: '0 4px',
                                                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: '9px', fontFamily: 'var(--brio-font-family)',
                                                          background: 'var(--brio-gold-light, #fef9e7)', color: 'var(--brio-gold, #9F902A)',
                                                        }}>{fmt}</div>
                                                        <EmailStatusMarker
                                                          status={deriveEmailStatus(stage.key, 'invoiceDate', psInvDate, { partId: part.id })}
                                                          onChange={(s) => updateEmailStatus(stage.key, 'invoiceDate', s, { partId: part.id })} />
                                                      </div>
                                                    );
                                                  }
                                                  const partInputValue = (canEditProject && psInvDate) ? (part.invoiceDate || '') : effectiveDate;
                                                  return (
                                                    <div style={{ display: 'flex', alignItems: 'center' }}>
                                                      <CompactDateInput
                                                        value={partInputValue}
                                                        onChange={(e) => {
                                                          handleDateChangeWithEmail(stage.key, 'invoiceDate', e.target.value, part.name, { partId: part.id, oldInvoiceValue: part.invoiceDate });
                                                        }} />
                                                      <EmailStatusMarker
                                                        status={deriveEmailStatus(stage.key, 'invoiceDate', partInputValue, { partId: part.id })}
                                                        onChange={(s) => updateEmailStatus(stage.key, 'invoiceDate', s, { partId: part.id })} />
                                                    </div>
                                                  );
                                                })()}
                                              </td>
                                              <td className={getTimelineCellColor({ columnKey: 'paidDate', value: part.paidDate || partSummary?.hasPaidConfirmed })}>
                                                {(() => {
                                                  // Paid: lock when invoice has paid_confirmed (admin/supervisor can still override)
                                                  if (partSummary?.hasPaidConfirmed && !canEditProject) {
                                                    const displayDate = partSummary.paidDate || part.paidDate || '';
                                                    const datePts = displayDate ? displayDate.split('-') : [];
                                                    const formatted = datePts.length === 3
                                                      ? `${datePts[0]}-${datePts[1]}-${datePts[2]}`
                                                      : '—';
                                                    return (
                                                      <div
                                                        title="Payment confirmed via Invoices page — locked"
                                                        style={{
                                                          width: '100%', height: '22px', boxSizing: 'border-box',
                                                          border: '1px solid var(--brio-border)', padding: '0 4px',
                                                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: '9px', fontFamily: 'var(--brio-font-family)',
                                                          background: 'var(--brio-green-light, #f0fdf4)',
                                                          color: 'var(--brio-green, #16a34a)',
                                                          cursor: 'not-allowed',
                                                        }}
                                                      >
                                                        ✓ {formatted}
                                                      </div>
                                                    );
                                                  }
                                                  return (
                                                    <div style={{ display: 'flex', alignItems: 'center' }}>
                                                      <CompactDateInput
                                                        locked={partLocked}
                                                        value={partSummary?.hasPaidConfirmed ? (partSummary.paidDate || part.paidDate || '') : (part.paidDate || '')}
                                                        onChange={(e) => handleDateChangeWithEmail(stage.key, 'paidDate', e.target.value, part.name, { partId: part.id })} />
                                                      <EmailStatusMarker
                                                        status={deriveEmailStatus(stage.key, 'paidDate', part.paidDate, { partId: part.id })}
                                                        onChange={(s) => updateEmailStatus(stage.key, 'paidDate', s, { partId: part.id })} />
                                                    </div>
                                                  );
                                                })()}
                                              </td>
                                              {/* Status */}
                                              <td>
                                                <div className="brio-flex brio-items-center" style={{ gap: '4px' }}>
                                                  <span className={`brio-badge ${partStatus.color}`}>
                                                    {partStatus.label || partStatus.status}
                                                  </span>
                                                  {partIsPaid && canEditProject && (
                                                    <LockToggleButton locked={partLocked} onToggle={() => toggleStageUnlock(partKey)} name={part.name} />
                                                  )}
                                                </div>
                                              </td>
                                            </tr>
                                          );
                                        })}
                                        {/* Expanded sub-row (CC lifecycle panel) */}
                                        {showExpanded && (
                                          <tr>
                                            <td colSpan={COLUMN_COUNT} style={{ padding: 0, background: 'var(--brio-grey-light)' }}>
                                              <CCLifecyclePanel
                                                ccTimeline={timeline}
                                                onUpdate={updateCCTimeline}
                                                onResetCC={handleResetCC}
                                                isAdminOrSupervisor={canEditProject}
                                                isAdmin={isAdmin}
                                                monthlyFee={project.retainerConfig?.monthlyAmount || 0}
                                                fmtCurr={fmtCurr}
                                                invoiceSummary={invoiceSummary}
                                                projectId={currentProjectId}
                                                onOpenInvoiceWorkflow={(ccMonthKey, stageName, invoiceDate) => {
                                                  const ccWf = {
                                                    step: 'pdf_step',
                                                    stageKey: ccMonthKey,
                                                    stageName,
                                                    invoiceDate: invoiceDate || new Date().toISOString().slice(0, 10),
                                                    invoiceDocType: 'tax',
                                                    field: 'invoiceDate',
                                                    value: invoiceDate || new Date().toISOString().slice(0, 10),
                                                    eventType: 'invoice_sent',
                                                  };
                                                  if (!gateInvoiceWorkflowForFeeRevision(ccWf)) {
                                                    setInvoiceWorkflow(ccWf);
                                                  }
                                                }}
                                              />
                                            </td>
                                          </tr>
                                        )}
                                      </React.Fragment>
                                    );
                                  })}
                                  {/* ── Misc Fee Scope stage rows ── */}
                                  {(project.miscFeeScopes || [])
                                    .filter(scope => {
                                      if (!showAllocatedOnly) return true;
                                      const miscKey = `misc__${scope.id}`;
                                      const tl = project.stageTimeline?.[miscKey] || {};
                                      const b = tl.billing || {};
                                      const n = (v) => Number(v ?? 0) || 0;
                                      if (n(scope.amount) > 0) return true;
                                      if (n(b.lumpsum ?? b.lumpSum) > 0) return true;
                                      if (n(b.percent) > 0) return true;
                                      if (invoiceSummary[miscKey]) return true; // invoiced; dates alone never list a scope
                                      return (tl.partStages || []).some(p => {
                                        const pb = p.billing || {};
                                        return n(pb.percentOfStage) > 0 || n(pb.lumpsum ?? pb.lumpSum) > 0;
                                      });
                                    })
                                    .map((scope) => {
                                      const miscKey = `misc__${scope.id}`;
                                      const timeline = project.stageTimeline[miscKey] || {};
                                      const statusInfo = getStageStatusFromTimeline(timeline, invoiceSummary[miscKey]);
                                      const miscIsPaid = (statusInfo.label || statusInfo.status) === 'Paid';
                                      const miscLocked = miscIsPaid && canEditProject && !unlockedStages[miscKey];

                                      return (
                                        <React.Fragment key={miscKey}>
                                          <tr className="brio-row--hoverable">
                                            {/* Caret placeholder */}
                                            <td />
                                            {/* Stage name + add part button */}
                                            <td className="brio-font-medium">
                                              <div className="brio-flex brio-items-center brio-justify-between" style={{ gap: '2px' }}>
                                                <span title={scope.name || miscKey}>{getStageAbbreviation(scope.name || miscKey)}</span>
                                                {canEditProject && (
                                                  <button
                                                    onClick={(e) => { e.stopPropagation(); addPartStage(miscKey); }}
                                                    title={`Add part stage under ${scope.name || miscKey}`}
                                                    style={{
                                                      background: 'none', border: 'none', cursor: 'pointer',
                                                      padding: '0 2px', fontSize: '9px', color: 'var(--brio-grey)',
                                                      lineHeight: 1, flexShrink: 0,
                                                    }}
                                                  >
                                                    <Plus className="w-3 h-3" />
                                                  </button>
                                                )}
                                              </div>
                                            </td>
                                            {/* 7 date columns — identical to regular stages */}
                                            <td className={getTimelineCellColor({ columnKey: 'contractDate', value: timeline.contractDate })}>
                                              <div style={{ display: 'flex', alignItems: 'center' }}>
                                                <CompactDateInput
                                                  locked={miscLocked}
                                                  value={timeline.contractDate || ''}
                                                  onChange={(e) => {
                                                    handleDateChangeWithEmail(miscKey, 'contractDate', e.target.value, scope.name || miscKey);
                                                  }} />
                                                <EmailStatusMarker status="not_applicable" disabled />
                                              </div>
                                            </td>
                                            <td className={getTimelineCellColor({ columnKey: 'targetDate', value: timeline.targetDate })}>
                                              <div style={{ display: 'flex', alignItems: 'center' }}>
                                                <CompactDateInput
                                                  locked={miscLocked}
                                                  value={timeline.targetDate || ''}
                                                  onChange={(e) => handleTargetDateChange(miscKey, e.target.value, scope.name || miscKey)} />
                                                {miscLocked
                                                  ? <LockedDot title="Planned date locked (paid) — frozen from planning sync" />
                                                  : <EmailStatusMarker status="not_applicable" disabled />}
                                              </div>
                                            </td>
                                            <td className={getTimelineCellColor({ columnKey: 'actualDate', value: timeline.actualDate })}>
                                              <div style={{ display: 'flex', alignItems: 'center' }}>
                                                <CompactDateInput
                                                  locked={miscLocked}
                                                  value={timeline.actualDate || ''}
                                                  onChange={(e) => {
                                                    handleDateChangeWithEmail(miscKey, 'actualDate', e.target.value, scope.name || miscKey);
                                                  }} />
                                                <ActualStatusMarker
                                                  actualDate={timeline.actualDate}
                                                  plannedDate={timeline.targetDate}
                                                  status={deriveEmailStatus(miscKey, 'actualDate', timeline.actualDate)}
                                                  onChange={(s) => updateEmailStatus(miscKey, 'actualDate', s)} />
                                              </div>
                                            </td>
                                            <td className={getTimelineCellColor({ columnKey: 'proformaInvoiceDate', value: timeline.proformaInvoiceDate || invoiceSummary[miscKey]?.lastProformaInvoiceDate })}>
                                              {renderProformaCell(invoiceSummary[miscKey], timeline, miscKey, scope.name || miscKey, { locked: miscLocked })}
                                            </td>
                                            <td className={getTimelineCellColor({ columnKey: 'clientApprovalDate', value: timeline.clientApprovalDate })}>
                                              <div style={{ display: 'flex', alignItems: 'center' }}>
                                                <CompactDateInput
                                                  locked={miscLocked}
                                                  value={timeline.clientApprovalDate || ''}
                                                  onChange={(e) => {
                                                    handleDateChangeWithEmail(miscKey, 'clientApprovalDate', e.target.value, scope.name || miscKey);
                                                  }} />
                                                <EmailStatusMarker status="not_applicable" disabled />
                                              </div>
                                            </td>
                                            <td className={getTimelineCellColor({ columnKey: 'invoiceDate', value: timeline.invoiceDate || invoiceSummary[miscKey]?.lastIssuedInvoiceDate })}>
                                              {(() => {
                                                const stageSummary = invoiceSummary[miscKey];
                                                const invDate = stageSummary?.lastIssuedInvoiceDate || stageSummary?.lastInvoiceDate;
                                                // A timeline invoiceDate on a proforma-only stage belongs to the
                                                // Proforma column — don't echo it here as a tax invoice date.
                                                const timelineInvDate = (stageSummary?.hasProformaInvoice && !stageSummary?.hasIssuedInvoice) ? '' : (timeline.invoiceDate || '');
                                                const effectiveDate = invDate || timelineInvDate;
                                                if (miscLocked) return <LockedDateBox value={effectiveDate} />;
                                                if (stageSummary?.hasIssuedInvoice && invDate && !canEditProject) {
                                                  const fmtParts = invDate.split('-');
                                                  const formatted = fmtParts.length === 3 ? `${fmtParts[0]}-${fmtParts[1]}-${fmtParts[2]}` : invDate;
                                                  return (
                                                    <div style={{ display: 'flex', alignItems: 'center' }}>
                                                      <div
                                                        title="Invoice date from Invoices page"
                                                        style={{
                                                          width: '100%', height: '22px', boxSizing: 'border-box',
                                                          border: '1px solid var(--brio-border)', padding: '0 4px',
                                                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: '9px', fontFamily: 'var(--brio-font-family)',
                                                          background: 'var(--brio-gold-light, #fef9e7)',
                                                          color: 'var(--brio-gold, #9F902A)',
                                                        }}
                                                      >{formatted}</div>
                                                      <EmailStatusMarker
                                                        status={deriveEmailStatus(miscKey, 'invoiceDate', invDate)}
                                                        onChange={(s) => updateEmailStatus(miscKey, 'invoiceDate', s)} />
                                                    </div>
                                                  );
                                                }
                                                const miscInputValue = (canEditProject && invDate) ? (timeline.invoiceDate || '') : effectiveDate;
                                                return (
                                                  <div style={{ display: 'flex', alignItems: 'center' }}>
                                                    <CompactDateInput
                                                      value={miscInputValue}
                                                      onChange={(e) => {
                                                        handleDateChangeWithEmail(miscKey, 'invoiceDate', e.target.value, scope.name || miscKey, { oldInvoiceValue: timeline.invoiceDate });
                                                      }} />
                                                    <EmailStatusMarker
                                                      status={deriveEmailStatus(miscKey, 'invoiceDate', miscInputValue)}
                                                      onChange={(s) => updateEmailStatus(miscKey, 'invoiceDate', s)} />
                                                  </div>
                                                );
                                              })()}
                                            </td>
                                            <td className={getTimelineCellColor({ columnKey: 'paidDate', value: timeline.paidDate || invoiceSummary[miscKey]?.hasPaidConfirmed })}>
                                              {(() => {
                                                const stageSummary = invoiceSummary[miscKey];
                                                if (stageSummary?.hasPaidConfirmed && !canEditProject) {
                                                  const displayDate = stageSummary.paidDate || timeline.paidDate || '';
                                                  const datePts = displayDate ? displayDate.split('-') : [];
                                                  const formatted = datePts.length === 3
                                                    ? `${datePts[0]}-${datePts[1]}-${datePts[2]}`
                                                    : '—';
                                                  return (
                                                    <div
                                                      title="Payment confirmed via Invoices page — locked"
                                                      style={{
                                                        width: '100%', height: '22px', boxSizing: 'border-box',
                                                        border: '1px solid var(--brio-border)', padding: '0 4px',
                                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: '9px', fontFamily: 'var(--brio-font-family)',
                                                        background: 'var(--brio-green-light, #f0fdf4)',
                                                        color: 'var(--brio-green, #16a34a)',
                                                        cursor: 'not-allowed',
                                                      }}
                                                    >
                                                      ✓ {formatted}
                                                    </div>
                                                  );
                                                }
                                                return (
                                                  <div style={{ display: 'flex', alignItems: 'center' }}>
                                                    <CompactDateInput
                                                      locked={miscLocked}
                                                      value={stageSummary?.hasPaidConfirmed ? (stageSummary.paidDate || timeline.paidDate || '') : (timeline.paidDate || '')}
                                                      onChange={(e) => handleDateChangeWithEmail(miscKey, 'paidDate', e.target.value, scope.name || miscKey)} />
                                                    <EmailStatusMarker
                                                      status={deriveEmailStatus(miscKey, 'paidDate', timeline.paidDate)}
                                                      onChange={(s) => updateEmailStatus(miscKey, 'paidDate', s)} />
                                                  </div>
                                                );
                                              })()}
                                            </td>
                                            {/* Status */}
                                            <td>
                                              <div className="brio-flex brio-items-center" style={{ gap: '4px' }}>
                                                <span className={`brio-badge ${statusInfo.color}`}>
                                                  {statusInfo.label || statusInfo.status}
                                                </span>
                                                {miscIsPaid && canEditProject && (
                                                  <LockToggleButton locked={miscLocked} onToggle={() => toggleStageUnlock(miscKey)} name={scope.name || miscKey} />
                                                )}
                                              </div>
                                            </td>
                                          </tr>
                                          {/* ── Part stage rows for misc scope ── */}
                                          {(timeline.partStages || [])
                                            .filter(part => {
                                              if (!showAllocatedOnly) return true;
                                              const pb = part.billing || {};
                                              const n = (v) => Number(v ?? 0) || 0;
                                              return n(pb.percentOfStage) > 0 || n(pb.lumpsum ?? pb.lumpSum) > 0;
                                            })
                                            .sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { numeric: true, sensitivity: 'base' }))
                                            .map((part) => {
                                              const partKey = derivePartStageKey(miscKey, part.id);
                                              const partSummary = invoiceSummary[partKey];
                                              const partStatus = getStageStatusFromTimeline(part, partSummary);
                                              const partIsPaid = (partStatus.label || partStatus.status) === 'Paid';
                                              const partLocked = partIsPaid && canEditProject && !unlockedStages[partKey];
                                              return (
                                                <tr key={partKey} style={{ background: 'var(--brio-grey-light)', fontSize: '10px' }}>
                                                  <td />
                                                  <td style={{ paddingLeft: '20px' }}>
                                                    <div className="brio-flex brio-items-center" style={{ gap: '2px' }}>
                                                      <span className="brio-text-grey" style={{ fontSize: '9px' }}>↳</span>
                                                      {(editingPartName?.stageKey === miscKey && editingPartName?.partId === part.id) ? (
                                                        <input
                                                          autoFocus
                                                          type="text"
                                                          value={partNameValue}
                                                          placeholder="e.g. Main house, Architecture"
                                                          onChange={(e) => setPartNameValue(e.target.value)}
                                                          onClick={(e) => e.stopPropagation()}
                                                          onBlur={() => {
                                                            const v = partNameValue.trim();
                                                            if (v && v !== part.name) updatePartStageField(miscKey, part.id, 'name', v);
                                                            setEditingPartName(null);
                                                          }}
                                                          onKeyDown={(e) => {
                                                            if (e.key === 'Enter') {
                                                              const v = partNameValue.trim();
                                                              if (v && v !== part.name) updatePartStageField(miscKey, part.id, 'name', v);
                                                              setEditingPartName(null);
                                                            }
                                                            if (e.key === 'Escape') setEditingPartName(null);
                                                          }}
                                                          style={{ flex: 1, minWidth: 0, fontSize: '10px', fontWeight: 500, fontFamily: 'Raleway, sans-serif', border: '1px solid var(--brio-gold)', padding: '1px 4px', outline: 'none', background: '#fffef5', color: '#333' }}
                                                        />
                                                      ) : (
                                                        <span
                                                          className="brio-font-medium"
                                                          style={{ fontSize: '10px', cursor: canEditProject ? 'text' : 'default' }}
                                                          title={canEditProject ? `${part.name} — click to rename` : part.name}
                                                          onClick={canEditProject ? (e) => {
                                                            e.stopPropagation();
                                                            setEditingPartName({ stageKey: miscKey, partId: part.id });
                                                            setPartNameValue(part.name || '');
                                                          } : undefined}
                                                        >
                                                          {getPartStageAbbreviation(part.name)}
                                                        </span>
                                                      )}
                                                      {canEditProject && (
                                                        <button
                                                          onClick={(e) => { e.stopPropagation(); deletePartStage(miscKey, part.id); }}
                                                          title="Delete part stage"
                                                          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0 1px', flexShrink: 0 }}
                                                        >
                                                          <Trash2 className="w-3 h-3" style={{ color: 'var(--brio-red)' }} />
                                                        </button>
                                                      )}
                                                    </div>
                                                  </td>
                                                  <td className={getTimelineCellColor({ columnKey: 'contractDate', value: part.contractDate })}>
                                                    <div style={{ display: 'flex', alignItems: 'center' }}>
                                                      <CompactDateInput
                                                        locked={partLocked}
                                                        value={part.contractDate || ''}
                                                        onChange={(e) => {
                                                          handleDateChangeWithEmail(miscKey, 'contractDate', e.target.value, part.name, { partId: part.id });
                                                        }} />
                                                      <EmailStatusMarker status="not_applicable" disabled />
                                                    </div>
                                                  </td>
                                                  <td className={getTimelineCellColor({ columnKey: 'targetDate', value: part.targetDate })}>
                                                    <div style={{ display: 'flex', alignItems: 'center' }}>
                                                      <CompactDateInput
                                                        locked={partLocked}
                                                        value={part.targetDate || ''}
                                                        onChange={(e) => updatePartStageField(miscKey, part.id, 'targetDate', e.target.value)} />
                                                      {partLocked
                                                        ? <LockedDot title="Planned date locked (paid) — frozen from planning sync" />
                                                        : <EmailStatusMarker status="not_applicable" disabled />}
                                                    </div>
                                                  </td>
                                                  <td className={getTimelineCellColor({ columnKey: 'actualDate', value: part.actualDate })}>
                                                    <div style={{ display: 'flex', alignItems: 'center' }}>
                                                      <CompactDateInput
                                                        locked={partLocked}
                                                        value={part.actualDate || ''}
                                                        onChange={(e) => {
                                                          handleDateChangeWithEmail(miscKey, 'actualDate', e.target.value, part.name, { partId: part.id });
                                                        }} />
                                                      <ActualStatusMarker
                                                        actualDate={part.actualDate}
                                                        plannedDate={part.targetDate}
                                                        status={deriveEmailStatus(miscKey, 'actualDate', part.actualDate, { partId: part.id })}
                                                        onChange={(s) => updateEmailStatus(miscKey, 'actualDate', s, { partId: part.id })} />
                                                    </div>
                                                  </td>
                                                  <td className={getTimelineCellColor({ columnKey: 'proformaInvoiceDate', value: part.proformaInvoiceDate || partSummary?.lastProformaInvoiceDate })}>
                                                    {renderProformaCell(partSummary, part, miscKey, part.name, { partId: part.id, locked: partLocked })}
                                                  </td>
                                                  <td className={getTimelineCellColor({ columnKey: 'clientApprovalDate', value: part.clientApprovalDate })}>
                                                    <div style={{ display: 'flex', alignItems: 'center' }}>
                                                      <CompactDateInput
                                                        locked={partLocked}
                                                        value={part.clientApprovalDate || ''}
                                                        onChange={(e) => {
                                                          handleDateChangeWithEmail(miscKey, 'clientApprovalDate', e.target.value, part.name, { partId: part.id });
                                                        }} />
                                                      <EmailStatusMarker status="not_applicable" disabled />
                                                    </div>
                                                  </td>
                                                  <td className={getTimelineCellColor({ columnKey: 'invoiceDate', value: part.invoiceDate || partSummary?.lastIssuedInvoiceDate })}>
                                                    {(() => {
                                                      const psInvDate = partSummary?.lastIssuedInvoiceDate || partSummary?.lastInvoiceDate;
                                                      // A timeline invoiceDate on a proforma-only part belongs to the
                                                      // Proforma column — don't echo it here as a tax invoice date.
                                                      const psTimelineInvDate = (partSummary?.hasProformaInvoice && !partSummary?.hasIssuedInvoice) ? '' : (part.invoiceDate || '');
                                                      const effectiveDate = psInvDate || psTimelineInvDate;
                                                      if (partLocked) return <LockedDateBox value={effectiveDate} />;
                                                      if (partSummary?.hasIssuedInvoice && psInvDate && !canEditProject) {
                                                        const pp = psInvDate.split('-');
                                                        const fmt = pp.length === 3 ? `${pp[0]}-${pp[1]}-${pp[2]}` : psInvDate;
                                                        return (
                                                          <div style={{ display: 'flex', alignItems: 'center' }}>
                                                            <div title="Invoice date from Invoices page" style={{
                                                              width: '100%', height: '22px', boxSizing: 'border-box', border: '1px solid var(--brio-border)', padding: '0 4px',
                                                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: '9px', fontFamily: 'var(--brio-font-family)',
                                                              background: 'var(--brio-gold-light, #fef9e7)', color: 'var(--brio-gold, #9F902A)',
                                                            }}>{fmt}</div>
                                                            <EmailStatusMarker
                                                              status={deriveEmailStatus(miscKey, 'invoiceDate', psInvDate, { partId: part.id })}
                                                              onChange={(s) => updateEmailStatus(miscKey, 'invoiceDate', s, { partId: part.id })} />
                                                          </div>
                                                        );
                                                      }
                                                      const miscPartInputValue = (canEditProject && psInvDate) ? (part.invoiceDate || '') : effectiveDate;
                                                      return (
                                                        <div style={{ display: 'flex', alignItems: 'center' }}>
                                                          <CompactDateInput
                                                            value={miscPartInputValue}
                                                            onChange={(e) => {
                                                              handleDateChangeWithEmail(miscKey, 'invoiceDate', e.target.value, part.name, { partId: part.id, oldInvoiceValue: part.invoiceDate });
                                                            }} />
                                                          <EmailStatusMarker
                                                            status={deriveEmailStatus(miscKey, 'invoiceDate', miscPartInputValue, { partId: part.id })}
                                                            onChange={(s) => updateEmailStatus(miscKey, 'invoiceDate', s, { partId: part.id })} />
                                                        </div>
                                                      );
                                                    })()}
                                                  </td>
                                                  <td className={getTimelineCellColor({ columnKey: 'paidDate', value: part.paidDate || partSummary?.hasPaidConfirmed })}>
                                                    {(() => {
                                                      if (partSummary?.hasPaidConfirmed && !canEditProject) {
                                                        const displayDate = partSummary.paidDate || part.paidDate || '';
                                                        const datePts = displayDate ? displayDate.split('-') : [];
                                                        const formatted = datePts.length === 3
                                                          ? `${datePts[0]}-${datePts[1]}-${datePts[2]}`
                                                          : '—';
                                                        return (
                                                          <div
                                                            title="Payment confirmed via Invoices page — locked"
                                                            style={{
                                                              width: '100%', height: '22px',
                                                              border: '1px solid var(--brio-border)',
                                                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: '9px', fontFamily: 'var(--brio-font-family)',
                                                              background: 'var(--brio-green-light, #f0fdf4)',
                                                              color: 'var(--brio-green, #16a34a)',
                                                              cursor: 'not-allowed',
                                                            }}
                                                          >
                                                            ✓ {formatted}
                                                          </div>
                                                        );
                                                      }
                                                      return (
                                                        <div style={{ display: 'flex', alignItems: 'center' }}>
                                                          <CompactDateInput
                                                            locked={partLocked}
                                                            value={partSummary?.hasPaidConfirmed ? (partSummary.paidDate || part.paidDate || '') : (part.paidDate || '')}
                                                            onChange={(e) => handleDateChangeWithEmail(miscKey, 'paidDate', e.target.value, part.name, { partId: part.id })} />
                                                          <EmailStatusMarker
                                                            status={deriveEmailStatus(miscKey, 'paidDate', part.paidDate, { partId: part.id })}
                                                            onChange={(s) => updateEmailStatus(miscKey, 'paidDate', s, { partId: part.id })} />
                                                        </div>
                                                      );
                                                    })()}
                                                  </td>
                                                  <td>
                                                    <div className="brio-flex brio-items-center" style={{ gap: '4px' }}>
                                                      <span className={`brio-badge ${partStatus.color}`}>
                                                        {partStatus.label || partStatus.status}
                                                      </span>
                                                      {partIsPaid && canEditProject && (
                                                        <LockToggleButton locked={partLocked} onToggle={() => toggleStageUnlock(partKey)} name={part.name} />
                                                      )}
                                                    </div>
                                                  </td>
                                                </tr>
                                              );
                                            })}
                                        </React.Fragment>
                                      );
                                    })}
                                </tbody>
                              </BrioTable>
                            </BrioByCode>
                            )}

                            {/* Legend */}
                            <div className="brio-mt-s" style={{ padding: '16px', background: 'var(--brio-grey-light)' }}>
                              <h4 className="brio-font-bold brio-mb-s">Status Legend & Reminders</h4>
                              <div className="brio-flex brio-gap-s brio-flex-wrap brio-text-s">
                                <div className="brio-flex brio-items-center brio-gap-xs">
                                  <span className="brio-badge brio-badge--approved">Paid</span>
                                  <span className="brio-text-grey">Payment received</span>
                                </div>
                                <div className="brio-flex brio-items-center brio-gap-xs">
                                  <span className="brio-badge brio-badge--warning">Invoiced</span>
                                  <span className="brio-text-grey">Invoice sent, awaiting payment</span>
                                </div>
                                <div className="brio-flex brio-items-center brio-gap-xs">
                                  <span className="brio-badge brio-badge--info">Radar</span>
                                  <span className="brio-text-grey">Invoice date set, pending admin to send</span>
                                </div>
                                <div className="brio-flex brio-items-center brio-gap-xs">
                                  <span className="brio-badge brio-badge--pending">Approved</span>
                                  <span className="brio-text-grey">Client approved</span>
                                </div>
                                <div className="brio-flex brio-items-center brio-gap-xs">
                                  <span className="brio-badge brio-badge--pending">Completed</span>
                                  <span className="brio-text-grey">Work finished</span>
                                </div>
                                <div className="brio-flex brio-items-center brio-gap-xs">
                                  <span className="brio-badge brio-badge--pending">Planned</span>
                                  <span className="brio-text-grey">Planned date set</span>
                                </div>
                                <div className="brio-flex brio-items-center brio-gap-xs">
                                  <span className="brio-badge">Contract</span>
                                  <span className="brio-text-grey">Contract date only</span>
                                </div>
                              </div>
                              <div className="brio-mt-s brio-text-xs brio-text-grey" style={{ borderTop: '1px solid var(--brio-border)', paddingTop: '12px' }}>
                                <p>• <strong>Invoice Follow-up:</strong> Automatic reminder 7 working days after invoice date</p>
                                <p>• <strong>Weekly Payment Reminder:</strong> Sent weekly if unpaid 14+ days after invoice</p>
                                <p>• <strong>Auto-stop:</strong> Reminders stop automatically when paid date is entered</p>
                              </div>
                            </div>
                          </>
                        );
                      })()}
                    </>
                  )}
                </>
              );
            })()}
          </BrioAnalyticsSurface>

          {/* ── Recommended Actions Panel ── */}
          {canEditProject && (
            <BrioAnalyticsSurface padding="m">
              <RecommendedActionsPanel
                openActions={recActions.openActions}
                dismissedActions={recActions.dismissedActions}
                completedActions={recActions.completedActions}
                actionDefs={recActions.ACTION_DEFS}
                onDismiss={recActions.dismiss}
                onComplete={recActions.complete}
                onReopen={recActions.reopen}
                onLaunchAction={handleLaunchAction}
                loading={recActions.loading}
              />
            </BrioAnalyticsSurface>
          )}
        </div>
      </BrioFrame>

      {/* ── Recommended Next Actions Modal (intermediate step before any workflow) ── */}
      {nextActionsContext && (
        <RecommendedNextActionsModal
          context={nextActionsContext}
          actionDefs={recActions.ACTION_DEFS}
          onSelectAction={handleNextActionSelect}
          onSkip={handleNextActionSkip}
        />
      )}

      {/* ── Email Draft Modal (non-invoice fields like contractDate) ── */}
      {emailDraftPending && currentProjectMemo && (
        <EmailDraftModal
          client={currentClientMemo}
          project={currentProjectMemo}
          stageName={emailDraftPending.stageName}
          eventType={emailDraftPending.eventType}
          dateValue={emailDraftPending.value}
          customTemplates={customEmailTemplates}
          stages={sortedStages}
          onClose={() => setEmailDraftPending(null)}
          onLogEmail={(emailData) => {
            logEmail({
              ...emailData,
              projectId: currentProjectMemo.id,
              clientId: currentClientMemo?.id,
            });
          }}
          onSaveOnly={() => {
            const p = emailDraftPending;
            setEmailDraftPending(null);
            // Save the timeline date change (non-invoice field only)
            if (p.partId) {
              const parentTimeline = currentProjectMemo.stageTimeline?.[p.stageKey] || {};
              const parts = (parentTimeline.partStages || []).map(part =>
                part.id === p.partId ? { ...part, [p.field]: p.value || null } : part
              );
              const newTimeline = {
                ...currentProjectMemo.stageTimeline,
                [p.stageKey]: { ...parentTimeline, partStages: parts },
              };
              if (useSupabaseDB) {
                supabaseProjects.mergeProjectTimeline(currentProjectMemo.id, { [p.stageKey]: newTimeline[p.stageKey] }, currentProjectMemo.stageTimeline || {});
              }
              setProjects(prev => prev.map(pr =>
                pr.id === currentProjectMemo.id ? { ...pr, stageTimeline: newTimeline } : pr
              ));
            } else {
              const newTimeline = {
                ...currentProjectMemo.stageTimeline,
                [p.stageKey]: {
                  ...(currentProjectMemo.stageTimeline?.[p.stageKey] || {}),
                  [p.field]: p.value || null,
                },
              };
              if (useSupabaseDB) {
                supabaseProjects.mergeProjectTimeline(currentProjectMemo.id, { [p.stageKey]: newTimeline[p.stageKey] }, currentProjectMemo.stageTimeline || {});
              }
              setProjects(prev => prev.map(pr =>
                pr.id === currentProjectMemo.id ? { ...pr, stageTimeline: newTimeline } : pr
              ));
            }
          }}
          onSaveAndMail={() => {
            const p = emailDraftPending;
            setEmailDraftPending(null);
            // Same save logic as onSaveOnly (non-invoice field only)
            if (p.partId) {
              const parentTimeline = currentProjectMemo.stageTimeline?.[p.stageKey] || {};
              const parts = (parentTimeline.partStages || []).map(part =>
                part.id === p.partId ? { ...part, [p.field]: p.value || null } : part
              );
              const newTimeline = {
                ...currentProjectMemo.stageTimeline,
                [p.stageKey]: { ...parentTimeline, partStages: parts },
              };
              if (useSupabaseDB) {
                supabaseProjects.mergeProjectTimeline(currentProjectMemo.id, { [p.stageKey]: newTimeline[p.stageKey] }, currentProjectMemo.stageTimeline || {});
              }
              setProjects(prev => prev.map(pr =>
                pr.id === currentProjectMemo.id ? { ...pr, stageTimeline: newTimeline } : pr
              ));
            } else {
              const newTimeline = {
                ...currentProjectMemo.stageTimeline,
                [p.stageKey]: {
                  ...(currentProjectMemo.stageTimeline?.[p.stageKey] || {}),
                  [p.field]: p.value || null,
                },
              };
              if (useSupabaseDB) {
                supabaseProjects.mergeProjectTimeline(currentProjectMemo.id, { [p.stageKey]: newTimeline[p.stageKey] }, currentProjectMemo.stageTimeline || {});
              }
              setProjects(prev => prev.map(pr =>
                pr.id === currentProjectMemo.id ? { ...pr, stageTimeline: newTimeline } : pr
              ));
            }
            // Show confirmation dialog after mail app opens
            if (p.field && isFieldActive(p.field)) {
              setTimeout(() => {
                setEmailSentConfirmation({
                  stageKey: p.stageKey, field: p.field, stageName: p.stageName,
                  eventType: p.eventType, partId: p.partId,
                });
              }, 500);
            }
          }}
        />
      )}

      {/* ── Fee Revision Gate: RecalculationPreviewModal ── */}
      {recalcPreview && currentProjectMemo && (
        <RecalculationPreviewModal
          revision={recalcPreview.revision}
          project={currentProjectMemo}
          stages={sortedStages}
          stageTimeline={currentProjectMemo.stageTimeline}
          budgetAllocation={currentProjectMemo.budgetAllocation}
          invoicesHook={invoicesHook}
          currentInvoice={recalcPreview.syntheticInvoice}
          fmtCurr={fmtCurr}
          formatMoney={settingsHook?.formatMoney}
          getCurrentTotalFee={getCurrentTotalFee}
          mode="create"
          onIssue={(adjustmentAmount, explanationText, revision) => {
            // Roll-in: proceed to invoice pipeline (adjustment baked into base)
            const wf = recalcPreview.pendingWorkflow;
            setRecalcPreview(null);
            setInvoiceWorkflow({ ...wf, feeRevisionAdjustment: { adjustmentAmount, explanationText, revisionId: revision.id, approach: 'roll_in' } });
          }}
          onIssueWithSeparateAdjustment={(adjustmentAmount, explanationText, revision) => {
            // Separate: proceed to invoice pipeline (original base, separate adjustment later)
            const wf = recalcPreview.pendingWorkflow;
            setRecalcPreview(null);
            setInvoiceWorkflow({ ...wf, feeRevisionAdjustment: { adjustmentAmount, explanationText, revisionId: revision.id, approach: 'separate' } });
          }}
          onClose={() => setRecalcPreview(null)}
        />
      )}

      {/* ── Invoice Workflow: PDF Step ── */}
      {invoiceWorkflow?.step === 'pdf_step' && currentProjectMemo && (
        <InvoicePipelineModal
          project={currentProjectMemo}
          client={currentClientMemo}
          stageKey={invoiceWorkflow.stageKey}
          stageName={invoiceWorkflow.stageName}
          invoiceDate={invoiceWorkflow.invoiceDate}
          invoiceDocType={invoiceWorkflow.invoiceDocType}
          invoicesHook={invoicesHook}
          projectsById={projectNameLookup}
          fmtCurr={fmtCurr}
          onClose={() => setInvoiceWorkflow(null)}
          onComplete={(result) => {
            // Refresh invoice summary so timeline reflects the newly created invoice
            refreshInvoiceSummary(currentProjectMemo?.id);
            setInvoiceWorkflow(null);
          }}
          onOpenEmail={({ invoiceId, invoiceNumber, invoiceNetAmount, pdfBlob, pdfFilename }) => {
            // Transition to email step — do NOT open mailto directly
            setInvoiceWorkflow(prev => ({
              ...prev,
              step: 'email_step',
              invoiceId,
              invoiceNumber,
              invoiceNetAmount,
              pdfBlob,
              pdfFilename,
              pdfDownloaded: true,
            }));
          }}
        />
      )}

      {/* ── Invoice Workflow: Email Step (after PDF is ready) ── */}
      {invoiceWorkflow?.step === 'email_step' && currentProjectMemo && (() => {
        const wfPaymentTerms = currentClientMemo?.paymentTermsDays;
        const { dueDate: wfDueDate } = calculateDueDate(invoiceWorkflow.invoiceDate, wfPaymentTerms);
        return (
        <EmailDraftModal
          client={currentClientMemo}
          project={currentProjectMemo}
          stageName={invoiceWorkflow.stageName}
          eventType={invoiceWorkflow.eventType || 'invoice_sent'}
          dateValue={invoiceWorkflow.invoiceDate}
          customTemplates={customEmailTemplates}
          stages={sortedStages}
          invoice={{ id: invoiceWorkflow.invoiceId, invoiceNumber: invoiceWorkflow.invoiceNumber, invoiceDate: invoiceWorkflow.invoiceDate }}
          hasPdfAttachment={!!invoiceWorkflow.pdfFilename}
          pdfFilename={invoiceWorkflow.pdfFilename}
          extraVars={{
            invoiceNumber: invoiceWorkflow.invoiceNumber || '',
            invoiceDate: formatDateForEmail(invoiceWorkflow.invoiceDate || ''),
            dueDate: formatDateForEmail(wfDueDate || ''),
            paymentTermsDays: wfPaymentTerms != null ? String(wfPaymentTerms) : '',
            amount: invoiceWorkflow.invoiceNetAmount != null ? fmtCurr(invoiceWorkflow.invoiceNetAmount) : '',
          }}
          onClose={() => {
            // Refresh invoice summary when email modal closes
            refreshInvoiceSummary(currentProjectMemo?.id);
            setInvoiceWorkflow(null);
          }}
          onLogEmail={(emailData) => {
            logEmail({
              ...emailData,
              projectId: currentProjectMemo.id,
              clientId: currentClientMemo?.id,
              invoiceId: invoiceWorkflow.invoiceId,
            });
          }}
          onSaveOnly={() => {
            // Refresh invoice summary + close workflow
            refreshInvoiceSummary(currentProjectMemo?.id);
            setInvoiceWorkflow(null);
          }}
          onSaveAndMail={() => {
            // Date was already saved during the PDF step; close the workflow
            // mailto is opened by EmailDraftModal itself after calling this
            const wf = invoiceWorkflow;
            // Refresh invoice summary
            refreshInvoiceSummary(currentProjectMemo?.id);
            setInvoiceWorkflow(null);
            // Show confirmation dialog for invoice email
            if (wf?.stageKey) {
              const triggerField = wf.field || 'invoiceDate';
              setTimeout(() => {
                setEmailSentConfirmation({
                  stageKey: wf.stageKey, field: triggerField, stageName: wf.stageName,
                  eventType: wf.eventType || 'invoice_sent', partId: wf.partId || null,
                });
              }, 500);
            }
          }}
        />
        );
      })()}

      {/* ── Email Sent Confirmation Dialog ── */}
      {emailSentConfirmation && (
        <EmailSentConfirmationModal
          stageName={emailSentConfirmation.stageName}
          eventType={emailSentConfirmation.eventType}
          onConfirmSent={() => {
            updateEmailStatus(
              emailSentConfirmation.stageKey,
              emailSentConfirmation.field,
              'sent',
              { partId: emailSentConfirmation.partId }
            );
            setEmailSentConfirmation(null);
          }}
          onKeepPending={() => {
            // Keep current status (draft_opened or pending)
            setEmailSentConfirmation(null);
          }}
          onClose={() => setEmailSentConfirmation(null)}
        />
      )}

      {/* ── Timeline Workflow Guide ── */}
      {showWorkflowGuide && (
        <TimelineWorkflowGuide onClose={() => setShowWorkflowGuide(false)} />
      )}
    </div>
  );
}
